import React, { ReactNode, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import 'bootstrap-select';
import { Tooltip as BsTooltip } from 'bootstrap';
import {
  ChartOptions,
  Chart as ChartJS,
  ArcElement,
} from 'chart.js';
import { Chart, Doughnut, Line } from 'react-chartjs-2';
import { useParams, useNavigate } from 'react-router-dom';

import { MonitorDashboardResp } from 'domain/payload/response/monitor/MonitorDashboardResp';
import MonitorService from 'service/MonitorService';
import { useSelector } from 'react-redux';
import DateRangeFilter from 'component/DateRangeFilterDropdown';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import UserDetailModal from '../../component/userDetailModal/UserViewModal';
import AreaDetailModal from 'component/areaDetailModal/AreaDetailModal';
import DashboardDetailModal from '../dashboard/DashboardDetailModal';
import { MonitorByAreaResponse } from 'domain/payload/response/monitor/MonitorByAreaResp';
import { DashboardConfigProjectResp } from 'domain/payload/response/dashboard/DashboardConfigProjectResp';
import { MonitorDailyPerformanceResp } from 'domain/payload/response/monitor/MonitorDailyPerformanceResp';
import { MonitorProgressTrackerResp } from 'domain/payload/response/monitor/MonitorProgressTrackerResp';
import { MonitorDailyResultMapResp } from 'domain/payload/response/monitor/MonitorDailyResultMapResp';
import MapGL, { Marker, Popup, Source, Layer, MapRef } from 'react-map-gl/mapbox';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import { hasProjectAccess } from 'helpers/hasPrivilege';

ChartJS.register(ArcElement);

const AUTO_REFRESH_OPTIONS: DropdownOption[] = [
  { id: 0, name: 'Off' },
  { id: 10000, name: '10 seconds' },
  { id: 300000, name: '5 minutes' },
  { id: 600000, name: '10 minutes' },
];

interface PlaceholderProps {
  height?: number | string;
  children?: ReactNode;
}

function getRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ${diffInMinutes % 60}m ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ${diffInHours % 24}h ago`;
}

const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDailyDateDisplay(d: Date): string {
  return `${d.getDate()} ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDailyDateYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDailyYMD(ymd: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function isCurrentDailyDate(d: Date): boolean {
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

function buildResultTrackerChartData(resultTracker?: MonitorProgressTrackerResp[]) {
  if (!resultTracker || resultTracker.length === 0) {
    return { labels: [], datasets: [] };
  }

  const resultLabels = resultTracker.map((t) =>
    String(t.date).padStart(2, "0") + ":00"
  );

  const resultLineMap = new Map();
  const resultBarMap = new Map();

  resultTracker.forEach((pt) => {
    pt.lineTracker?.forEach((lt) => {
      if (!resultLineMap.has(lt.label)) {
        resultLineMap.set(lt.label, {
          type: "line",
          label: lt.label,
          borderColor: lt.color,
          data: [],
          borderWidth: 2,
          fill: false,
          yAxisID: "yRight",
          pointRadius: 1,
          pointHoverRadius: 5,
          hidden: lt.hidden,
        });
      }
      resultLineMap.get(lt.label).data.push(lt.value);
    });

    pt.barTracker?.forEach((bt) => {
      if (!resultBarMap.has(bt.label)) {
        resultBarMap.set(bt.label, {
          type: "bar",
          label: bt.label,
          backgroundColor: bt.color,
          data: [],
          yAxisID: "yLeft",
          hidden: bt.hidden,
        });
      }
      resultBarMap.get(bt.label).data.push(bt.value);
    });
  });

  return {
    labels: resultLabels,
    datasets: [...resultLineMap.values(), ...resultBarMap.values()],
  };
}

function StackedProgressBar({
  results,
  target = 0,
  height = 6
}: {
  results: { label?: string, value: string | number, color: string }[],
  target?: number,
  height?: number
}) {
  const excludedLabels = ['result', 'target'];
  const segments = results.filter(r => r.label && !excludedLabels.includes(r.label.toLowerCase()));

  const resultItem = results.find(r => r.label && r.label.toLowerCase() === 'result');
  const targetItem = results.find(r => r.label && r.label.toLowerCase() === 'target');

  const explicitTotal = target > 0 ? target :
    targetItem ? Number(targetItem.value) :
      resultItem ? Number(resultItem.value) : 0;

  const segmentsTotal = segments.reduce((acc, r) => acc + (Number(r.value) || 0), 0);

  const maxBarValue = explicitTotal > 0 ? explicitTotal : segmentsTotal;

  const trackStyle: React.CSSProperties = {
    height: `${height}px`,
    backgroundColor: '#f1f5f9',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.06)'
  };

  if (maxBarValue === 0) {
    return (
      <div className="progress w-100" style={trackStyle} />
    );
  }

  return (
    <div className="progress w-100" style={trackStyle}>
      {segments.map((r, i) => {
        const val = Number(r.value) || 0;
        if (val === 0) return null;
        const percentage = Math.min((val / maxBarValue) * 100, 100);
        return (
          <div
            key={i}
            className="progress-bar"
            style={{
              width: `${percentage}%`,
              backgroundColor: r.color || '#cbd5e1',
              borderRadius: segments.length === 1 ? '10px' : '0px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              transition: 'width 0.3s ease'
            }}
          />
        );
      })}
    </div>
  );
}


function Placeholder({ height = 180, children }: PlaceholderProps) {
  return (
    <div
      className="p-3 border-0 shadow-sm w-100"
      style={{ height: height, background: '#ffffff', borderRadius: '16px' }}
    >
      {children}
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div
      className="top-0 position-absolute start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 10 }}
    >
      <div
        className="spinner-border text-primary"
        role="status"
        style={{ width: 60, height: 60 }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const LINE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // orange
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#ef4444', // red
  '#14b8a6', // teal
  '#6366f1', // indigo
  '#f43f5e', // rose
];

const verticalHoverPlugin = {
  id: 'verticalHoverBg',
  afterDraw: (chart: any) => {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart.tooltip._active[0];
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      const x = activePoint.element.x;

      ctx.save();

      // 1. Draw a soft column highlight background
      ctx.fillStyle = 'rgba(59, 130, 246, 0.04)';
      const meta = chart.getDatasetMeta(0);
      if (meta && meta.data) {
        const pointsCount = meta.data.length;
        let width = 30;
        if (pointsCount > 1) {
          width = (chartArea.right - chartArea.left) / (pointsCount - 1);
        }
        ctx.fillRect(x - width / 2, chartArea.top, width, chartArea.bottom - chartArea.top);
      }

      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)';
      ctx.stroke();

      ctx.restore();
    }
  }
};

interface DailyPerformanceMapViewProps {
  performanceData: MonitorDailyPerformanceResp[];
  resultMapData: MonitorDailyResultMapResp[];
  onViewOfficerDetail: (userId: string) => void;
  onOpenResultDetail?: (item: any) => void;
}

const DailyPerformanceMapView: React.FC<DailyPerformanceMapViewProps> = ({
  performanceData,
  resultMapData,
  onViewOfficerDetail,
  onOpenResultDetail,
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const [selectedResult, setSelectedResult] = useState<MonitorDailyResultMapResp | null>(null);
  const [hoveredResult, setHoveredResult] = useState<MonitorDailyResultMapResp | null>(null);

  const getElapsedTime = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return '-';
    const start = new Date(startTime.replace(' ', 'T')).getTime();
    const end = new Date(endTime.replace(' ', 'T')).getTime();
    if (isNaN(start) || isNaN(end) || end < start) return '-';

    const totalSec = Math.floor((end - start) / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const officers = useMemo(() => {
    const map = new Map<string, {
      key: string;
      name: string;
      userId?: string;
      colorHex: string;
      colorIcon: string;
      profilePictureUrl?: string;
      count: number;
    }>();

    performanceData.forEach((p) => {
      const key = (p.name || p.userId || '').toLowerCase().trim();
      if (!key) return;
      map.set(key, {
        key,
        name: p.name || 'Unknown Officer',
        userId: p.userId,
        colorHex: p.colorHex || '7986CB',
        colorIcon: p.colorIcon || 'icon-marker.svg',
        profilePictureUrl: p.profilePictureUrl,
        count: 0,
      });
    });

    resultMapData.forEach((r) => {
      const key = (r.userName || r.userEmail || '').toLowerCase().trim();
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        if (!existing.colorHex && r.colorHex) existing.colorHex = r.colorHex;
        if (!existing.colorIcon && r.colorIcon) existing.colorIcon = r.colorIcon;
      } else {
        map.set(key, {
          key,
          name: r.userName || 'Unknown Officer',
          colorHex: r.colorHex || '81C784',
          colorIcon: r.colorIcon || 'icon-marker.svg',
          count: 1,
        });
      }
    });

    return Array.from(map.values());
  }, [performanceData, resultMapData]);

  const [selectedOfficerKeys, setSelectedOfficerKeys] = useState<Set<string>>(() => {
    return new Set(officers.map((o) => o.key));
  });

  useEffect(() => {
    if (officers.length > 0) {
      setSelectedOfficerKeys(new Set(officers.map((o) => o.key)));
    }
  }, [officers]);

  const toggleOfficer = (key: string) => {
    setSelectedOfficerKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isAllSelected = officers.length > 0 && selectedOfficerKeys.size === officers.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedOfficerKeys(new Set());
    } else {
      setSelectedOfficerKeys(new Set(officers.map((o) => o.key)));
    }
  };

  const filteredMapData = useMemo(() => {
    return resultMapData.filter((r) => {
      const key = (r.userName || r.userEmail || '').toLowerCase().trim();
      return selectedOfficerKeys.has(key);
    });
  }, [resultMapData, selectedOfficerKeys]);

  const isMapInitialized = useRef(false);

  const handleMapLoad = useCallback(() => {
    if (!mapRef.current || filteredMapData.length === 0 || isMapInitialized.current) return;
    const lngs = filteredMapData.map((t) => t.longitude).filter(Boolean);
    const lats = filteredMapData.map((t) => t.latitude).filter(Boolean);
    if (lngs.length === 0 || lats.length === 0) return;

    mapRef.current.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 50, duration: 600 }
    );
    isMapInitialized.current = true;
  }, [filteredMapData]);

  const prevOfficerKeys = useRef<string>('');
  useEffect(() => {
    const keysStr = Array.from(selectedOfficerKeys).sort().join(',');
    if (prevOfficerKeys.current && prevOfficerKeys.current !== keysStr) {
      if (mapRef.current && filteredMapData.length > 0) {
        const lngs = filteredMapData.map((t) => t.longitude).filter(Boolean);
        const lats = filteredMapData.map((t) => t.latitude).filter(Boolean);
        if (lngs.length > 0 && lats.length > 0) {
          mapRef.current.fitBounds(
            [
              [Math.min(...lngs), Math.min(...lats)],
              [Math.max(...lngs), Math.max(...lats)],
            ],
            { padding: 50, duration: 400 }
          );
        }
      }
    }
    prevOfficerKeys.current = keysStr;
  }, [selectedOfficerKeys, filteredMapData]);

  const getMarkerIconUrl = (colorIcon?: string) => {
    if (!colorIcon) return '/assets/markers/icon-marker.svg';
    if (colorIcon.startsWith('http://') || colorIcon.startsWith('https://')) {
      return colorIcon;
    }
    const cleanIcon = colorIcon.replace(/^\/?(assets\/)?(markers\/)?(marker\/)?/, '');
    return `/assets/markers/${cleanIcon}`;
  };

  const [showRoutes, setShowRoutes] = useState<boolean>(true);
  const [showIntervals, setShowIntervals] = useState<boolean>(false);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);

  const { officerRoutesGeoJSON, routeSegments } = useMemo(() => {
    if (!showRoutes || filteredMapData.length < 2) {
      return { officerRoutesGeoJSON: null, routeSegments: [] };
    }

    const officerGroups = new Map<string, MonitorDailyResultMapResp[]>();

    filteredMapData.forEach((item) => {
      const key = (item.userName || item.userEmail || 'unknown').toLowerCase().trim();
      if (!item.latitude || !item.longitude) return;
      if (!officerGroups.has(key)) {
        officerGroups.set(key, []);
      }
      officerGroups.get(key)!.push(item);
    });

    const features: any[] = [];
    const segments: {
      id: string;
      segmentIndex: number;
      officerKey: string;
      officerName: string;
      seqA: number;
      seqB: number;
      colorHex: string;
      midpoint: { lat: number; lng: number };
      gapTimeSeconds: number | null;
      gapTimeString: string;
      elapsedTimeB: string | null;
    }[] = [];

    let globalSegmentIdx = 0;

    officerGroups.forEach((items, officerKey) => {
      if (items.length < 2) return;

      const sorted = [...items].sort((a, b) => {
        const timeA = new Date((a.submitDate || a.startTime || '').replace(' ', 'T')).getTime();
        const timeB = new Date((b.submitDate || b.startTime || '').replace(' ', 'T')).getTime();
        return timeA - timeB;
      });

      const colorHex = sorted[0].colorHex ? `#${sorted[0].colorHex.replace('#', '')}` : '#3b82f6';

      for (let i = 0; i < sorted.length - 1; i++) {
        const itemA = sorted[i];
        const itemB = sorted[i + 1];

        const startLng = itemA.longitude;
        const startLat = itemA.latitude;
        const endLng = itemB.longitude;
        const endLat = itemB.latitude;

        const timeA = new Date((itemA.submitDate || itemA.endTime || itemA.startTime || '').replace(' ', 'T')).getTime();
        const timeB = new Date((itemB.startTime || itemB.submitDate || '').replace(' ', 'T')).getTime();

        let gapTimeSec: number | null = null;
        let gapStr = '-';

        if (!isNaN(timeA) && !isNaN(timeB) && timeB >= timeA) {
          gapTimeSec = Math.floor((timeB - timeA) / 1000);
          const hours = Math.floor(gapTimeSec / 3600);
          const minutes = Math.floor((gapTimeSec % 3600) / 60);
          const seconds = gapTimeSec % 60;

          if (hours > 0) {
            gapStr = `${hours}h ${minutes}m ${seconds}s`;
          } else if (minutes > 0) {
            gapStr = `${minutes}m ${seconds}s`;
          } else {
            gapStr = `${seconds}s`;
          }
        }

        const elapsedTimeB = getElapsedTime(itemB.startTime, itemB.endTime);

        const segmentIdx = globalSegmentIdx++;

        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [startLng, startLat],
              [endLng, endLat],
            ],
          },
          properties: {
            segmentIndex: segmentIdx,
            officerKey,
            colorHex,
            officerName: itemA.userName,
          },
        });

        segments.push({
          id: `${officerKey}-${i}`,
          segmentIndex: segmentIdx,
          officerKey,
          officerName: itemA.userName || 'Officer',
          seqA: i + 1,
          seqB: i + 2,
          colorHex,
          midpoint: {
            lat: (startLat + endLat) / 2,
            lng: (startLng + endLng) / 2,
          },
          gapTimeSeconds: gapTimeSec,
          gapTimeString: gapStr,
          elapsedTimeB: elapsedTimeB !== '-' ? elapsedTimeB : null,
        });
      }
    });

    return {
      officerRoutesGeoJSON: {
        type: 'FeatureCollection',
        features,
      },
      routeSegments: segments,
    };
  }, [filteredMapData, showRoutes]);

  const markerSequenceMap = useMemo(() => {
    const map = new Map<string, { seq: number; total: number; isStart: boolean; isEnd: boolean }>();

    const officerGroups = new Map<string, MonitorDailyResultMapResp[]>();

    filteredMapData.forEach((item) => {
      const key = (item.userName || item.userEmail || 'unknown').toLowerCase().trim();
      if (!item.latitude || !item.longitude) return;
      if (!officerGroups.has(key)) {
        officerGroups.set(key, []);
      }
      officerGroups.get(key)!.push(item);
    });

    officerGroups.forEach((items) => {
      const sorted = [...items].sort((a, b) => {
        const timeA = new Date((a.submitDate || a.startTime || '').replace(' ', 'T')).getTime();
        const timeB = new Date((b.submitDate || b.startTime || '').replace(' ', 'T')).getTime();
        return timeA - timeB;
      });

      const total = sorted.length;

      sorted.forEach((item, index) => {
        map.set(item.id, {
          seq: index + 1,
          total,
          isStart: index === 0,
          isEnd: index === total - 1 && total > 1,
        });
      });
    });

    return map;
  }, [filteredMapData]);

  const defaultCenter = useMemo(() => {
    const valid = resultMapData.find((r) => r.latitude && r.longitude);
    if (valid) {
      return { latitude: valid.latitude, longitude: valid.longitude, zoom: 11 };
    }
    return { latitude: -6.2088, longitude: 106.8456, zoom: 10 };
  }, [resultMapData]);

  return (
    <div className="d-flex flex-column h-100">
      <div className="mb-2 pb-2 border-bottom">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <span className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            Officer Legend ({officers.length})
          </span>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={() => setShowRoutes((prev) => !prev)}
              className={`btn btn-sm d-inline-flex align-items-center gap-1 transition-all ${showRoutes ? 'btn-primary text-white' : 'btn-outline-secondary text-secondary'
                }`}
              style={{ fontSize: '0.68rem', padding: '1px 8px', height: '22px', borderRadius: '6px' }}
            >
              <i className="fa-solid fa-route" style={{ fontSize: '0.65rem' }}></i>
              {showRoutes ? 'Routes On' : 'Show Routes'}
            </button>

            <button
              type="button"
              onClick={() => setShowIntervals((prev) => !prev)}
              className={`btn btn-sm d-inline-flex align-items-center gap-1 transition-all ${showIntervals ? 'btn-info text-white' : 'btn-outline-secondary text-secondary'
                }`}
              style={{ fontSize: '0.68rem', padding: '1px 8px', height: '22px', borderRadius: '6px' }}
            >
              <i className="fa-regular fa-clock" style={{ fontSize: '0.65rem' }}></i>
              {showIntervals ? 'Intervals On' : 'Show Intervals'}
            </button>
            {officers.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-primary fw-semibold"
                style={{ fontSize: '0.75rem' }}
                onClick={toggleSelectAll}
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-1 align-items-center">
          {officers.length === 0 ? (
            <span className="text-muted small">No officer data available</span>
          ) : (
            officers.map((officer) => {
              const isSelected = selectedOfficerKeys.has(officer.key);
              const hexColor = officer.colorHex ? `#${officer.colorHex.replace('#', '')}` : '#7986CB';

              return (
                <button
                  key={officer.key}
                  type="button"
                  onClick={() => toggleOfficer(officer.key)}
                  className={`btn btn-sm d-inline-flex align-items-center gap-1 transition-all ${isSelected ? '' : 'opacity-50'
                    }`}
                  style={{
                    fontSize: '0.68rem',
                    padding: '1px 6px',
                    height: '22px',
                    lineHeight: '1',
                    backgroundColor: isSelected ? `${hexColor}12` : '#f8f9fa',
                    border: `1px solid ${isSelected ? hexColor : '#e2e8f0'}`,
                    color: isSelected ? '#1e293b' : '#64748b',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {officer.profilePictureUrl ? (
                    <img
                      src={officer.profilePictureUrl}
                      alt=""
                      className="rounded-circle"
                      style={{ width: 12, height: 12, objectFit: 'cover' }}
                    />
                  ) : (
                    <span
                      className="rounded-circle d-inline-block"
                      style={{
                        width: 7,
                        height: 7,
                        backgroundColor: hexColor,
                        border: '1.5px solid rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span className="fw-medium text-truncate" style={{ maxWidth: '100px' }}>
                    {officer.name}
                  </span>
                  <span
                    className="badge"
                    style={{
                      fontSize: '0.58rem',
                      padding: '1px 4px',
                      backgroundColor: isSelected ? hexColor : '#cbd5e1',
                      color: '#ffffff',
                      fontWeight: 600,
                      borderRadius: '4px',
                    }}
                  >
                    {officer.count}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="position-relative rounded-3 overflow-hidden border" style={{ height: '420px' }}>
        <MapGL
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={defaultCenter}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          onLoad={handleMapLoad}
          onMouseMove={(e) => {
            if (!showRoutes) return;
            const features = e.target.queryRenderedFeatures(e.point, {
              layers: ['officer-routes-layer'],
            });
            if (features.length > 0) {
              const idx = features[0].properties?.segmentIndex;
              if (idx !== undefined && idx !== null) {
                setHoveredSegmentIndex(idx);
              }
              e.target.getCanvas().style.cursor = 'pointer';
            } else {
              setHoveredSegmentIndex(null);
              e.target.getCanvas().style.cursor = '';
            }
          }}
          onMouseLeave={() => {
            setHoveredSegmentIndex(null);
          }}
        >
          {showRoutes && officerRoutesGeoJSON && officerRoutesGeoJSON.features.length > 0 && (
            <Source id="officer-routes-source" type="geojson" data={officerRoutesGeoJSON as any}>
              <Layer
                id="officer-routes-layer"
                type="line"
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round',
                }}
                paint={{
                  'line-color': ['get', 'colorHex'],
                  'line-width': [
                    'case',
                    ['==', ['get', 'segmentIndex'], hoveredSegmentIndex ?? -1], 8,
                    5.5,
                  ],
                  'line-opacity': [
                    'case',
                    ['==', ['get', 'segmentIndex'], hoveredSegmentIndex ?? -1], 0.95,
                    0.85,
                  ],
                }}
              />
            </Source>
          )}

          {showRoutes &&
            routeSegments.map((segment) => {
              const isHovered = hoveredSegmentIndex === segment.segmentIndex;
              if (!showIntervals && !isHovered) return null;

              return (
                <Marker
                  key={`gap-${segment.id}`}
                  longitude={segment.midpoint.lng}
                  latitude={segment.midpoint.lat}
                  anchor="center"
                >
                  <div
                    style={{
                      background: '#ffffff',
                      border: `1.5px solid ${segment.colorHex}`,
                      borderRadius: '999px',
                      padding: '1px 6px',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#1e293b',
                      pointerEvents: 'none',
                      boxShadow: isHovered
                        ? '0 4px 12px rgba(0,0,0,0.25)'
                        : '0 2px 6px rgba(0,0,0,0.18)',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      zIndex: isHovered ? 300 : 50,
                      transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                      transition: 'transform 0.15s ease',
                    }}
                    title={`Jeda perjalanan #${segment.seqA} ke #${segment.seqB} (${segment.officerName})`}
                  >
                    <i className="fa-regular fa-clock text-primary" style={{ fontSize: 9 }}></i>
                    <span>{segment.gapTimeString}</span>
                  </div>
                </Marker>
              );
            })}
          {filteredMapData.map((item, index) => {
            const isSelected = selectedResult?.id === item.id;
            const isHovered = hoveredResult?.id === item.id;
            const isActive = isSelected || isHovered;
            const seqInfo = markerSequenceMap.get(item.id);
            const hexColor = item.colorHex ? `#${item.colorHex.replace('#', '')}` : '#3b82f6';

            return (
              <Marker
                key={item.id || index}
                longitude={item.longitude}
                latitude={item.latitude}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setHoveredResult(null);
                  if (onOpenResultDetail) {
                    onOpenResultDetail({ id: item.id });
                  }
                  mapRef.current?.flyTo({
                    center: [item.longitude, item.latitude],
                    duration: 500,
                    offset: [0, 60],
                  });
                }}
              >
                <div
                  onMouseEnter={() => setHoveredResult(item)}
                  onMouseLeave={() => setHoveredResult(null)}
                  style={{ display: 'inline-block', position: 'relative' }}
                >
                  <img
                    src={getMarkerIconUrl(item.colorIcon)}
                    style={{
                      width: isActive ? 34 : 28,
                      height: isActive ? 34 : 28,
                      cursor: 'pointer',
                      filter: isActive
                        ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))'
                        : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15))',
                      transform: isActive ? 'scale(1.15)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                    }}
                    alt={item.name}
                  />

                  {seqInfo && (
                    <span
                      className="position-absolute shadow-sm"
                      style={{
                        top: '-4px',
                        right: '-6px',
                        fontSize: '0.62rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 3px',
                        borderRadius: '999px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: seqInfo.isStart ? '#10b981' : seqInfo.isEnd ? '#ef4444' : hexColor,
                        color: '#ffffff',
                        border: '1.5px solid #ffffff',
                        fontWeight: 700,
                        zIndex: 10,
                        lineHeight: 1,
                        cursor: 'pointer',
                      }}
                      title={`Urutan kunjungan #${seqInfo.seq} dari ${seqInfo.total}${seqInfo.isStart ? ' (Point Awal)' : seqInfo.isEnd ? ' (Point Akhir)' : ''
                        }`}
                    >
                      {seqInfo.seq}
                    </span>
                  )}
                </div>
              </Marker>
            );
          })}

          {(() => {
            const activeResult = hoveredResult;
            if (!activeResult?.latitude || !activeResult?.longitude) return null;

            const activeSeqInfo = markerSequenceMap.get(activeResult.id);

            return (
              <Popup
                longitude={activeResult.longitude}
                latitude={activeResult.latitude}
                anchor="bottom"
                offset={[0, -34]}
                onClose={() => {
                  setHoveredResult(null);
                }}
                closeOnClick={false}
                closeButton={false}
                maxWidth="280px"
              >
                <div
                  style={{
                    padding: '4px 6px',
                    fontSize: '0.72rem',
                    lineHeight: '1.35',
                    color: '#1e293b',
                    minWidth: '220px',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-1 pb-1 mb-1 border-bottom">
                    <div className="d-flex align-items-center gap-1.5 min-w-0">
                      <span
                        className="rounded-circle d-inline-block flex-shrink-0"
                        style={{
                          width: 8,
                          height: 8,
                          backgroundColor: activeResult.colorHex ? `#${activeResult.colorHex.replace('#', '')}` : '#3b82f6',
                        }}
                      />
                      <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.8rem' }} title={activeResult.name}>
                        {activeResult.name || 'Unnamed Result'}
                      </span>
                    </div>
                    <span
                      className="badge bg-light text-secondary border font-monospace"
                      style={{ fontSize: '0.6rem', padding: '1px 4px', flexShrink: 0 }}
                      title={`Full ID: ${activeResult.id}`}
                    />
                  </div>

                  <div className="d-flex flex-column gap-1">
                    {activeSeqInfo && (
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                          <i className="fa-solid fa-list-ol text-primary" style={{ fontSize: '0.65rem' }}></i>
                          Urutan
                        </span>
                        <span
                          className={`badge rounded-pill ${activeSeqInfo.isStart
                            ? 'bg-success-subtle text-success-emphasis border border-success-subtle'
                            : activeSeqInfo.isEnd
                              ? 'bg-danger-subtle text-danger-emphasis border border-danger-subtle'
                              : 'bg-primary-subtle text-primary-emphasis border border-primary-subtle'
                            } fw-bold`}
                          style={{ fontSize: '0.65rem', padding: '1px 6px' }}
                        >
                          #{activeSeqInfo.seq} dari {activeSeqInfo.total}{' '}
                          {activeSeqInfo.isStart ? '(Awal)' : activeSeqInfo.isEnd ? '(Akhir)' : ''}
                        </span>
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                        <i className="fa-solid fa-user text-primary" style={{ fontSize: '0.65rem' }}></i>
                        User
                      </span>
                      <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }}>
                        {activeResult.userName || 'N/A'}
                      </span>
                    </div>

                    {activeResult.userEmail && (
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                          <i className="fa-regular fa-envelope text-info" style={{ fontSize: '0.65rem' }}></i>
                          Email
                        </span>
                        <span className="text-secondary text-truncate" style={{ maxWidth: '140px' }}>
                          {activeResult.userEmail}
                        </span>
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                        <i className="fa-regular fa-calendar-check text-success" style={{ fontSize: '0.65rem' }}></i>
                        Submit Date
                      </span>
                      <span className="fw-medium text-dark">{activeResult.submitDate || '-'}</span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                        <i className="fa-solid fa-stopwatch text-warning" style={{ fontSize: '0.65rem' }}></i>
                        Elapsed Time
                      </span>
                      <span
                        className="badge rounded-pill bg-warning-subtle text-warning-emphasis border border-warning-subtle fw-bold"
                        style={{ fontSize: '0.65rem', padding: '1px 5px' }}
                      >
                        {getElapsedTime(activeResult.startTime, activeResult.endTime)}
                      </span>
                    </div>
                  </div>
                </div>
              </Popup>
            );
          })()}
        </MapGL>

        <div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 1 }}>
          <div className="bg-white rounded-pill shadow-sm px-2 py-1 d-flex align-items-center gap-1 border">
            <i className="fa-solid fa-map-pin text-primary" style={{ fontSize: '0.75rem' }}></i>
            <small className="fw-semibold text-dark" style={{ fontSize: '0.75rem' }}>
              {filteredMapData.length} Locations
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AreaDescriptionWithTooltipProps {
  text: string;
  fontSize?: string;
  maxWidth?: string;
}

const AreaDescriptionWithTooltip: React.FC<AreaDescriptionWithTooltipProps> = ({
  text,
  fontSize = '0.7rem',
  maxWidth,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => {
      if (el) {
        setIsOverflow(el.scrollWidth > el.clientWidth);
      }
    };

    checkOverflow();
    const rafId = requestAnimationFrame(checkOverflow);
    const timeoutId = setTimeout(checkOverflow, 200);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => checkOverflow());
      resizeObserver.observe(el);
    }

    window.addEventListener('resize', checkOverflow);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text]);

  useEffect(() => {
    if (!iconRef.current || !isOverflow) return;

    const el = iconRef.current;
    let tooltipInstance: any = null;

    try {
      const TooltipClass = (window as any).bootstrap?.Tooltip || BsTooltip;
      if (TooltipClass) {
        tooltipInstance = new TooltipClass(el, {
          title: text,
          placement: 'top',
          trigger: 'hover',
        });
      }
    } catch (err) {
      console.warn('Bootstrap Tooltip error:', err);
    }

    return () => {
      if (tooltipInstance) {
        try {
          tooltipInstance.dispose();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [text, isOverflow]);

  return (
    <div
      className="d-flex align-items-center gap-1"
      style={{ minWidth: 0, maxWidth: maxWidth || '100%' }}
    >
      <span
        ref={textRef}
        className="text-muted text-truncate"
        style={{ fontSize, flex: '1 1 auto', minWidth: 0 }}
      >
        {text}
      </span>
      {isOverflow && (
        <span
          ref={iconRef}
          className="text-muted flex-shrink-0 d-inline-flex align-items-center"
          style={{ cursor: 'pointer', fontSize, lineHeight: 1, opacity: 0.8 }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
          onClick={(e) => e.stopPropagation()}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-title={text}
          title={text}
        >
          <i className="fa-solid fa-circle-info text-secondary" style={{ fontSize: '0.85em' }}></i>
        </span>
      )}
    </div>
  );
};

const Monitor: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [projectList, setProjectList] = useState<DashboardConfigProjectResp[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const [monitorData, setMonitorData] = useState<MonitorDashboardResp>()
  const [matrixViewMode, setMatrixViewMode] = useState<'table' | 'chart'>('table');
  const [matrixValueMode, setMatrixValueMode] = useState<'daily' | 'cumulative'>('daily');
  const [matrixMetricMode, setMatrixMetricMode] = useState<'count' | 'percentage'>('count');
  const [matrixGroupMode, setMatrixGroupMode] = useState<'officer' | 'tribe' | 'group'>('officer');
  const { navRef: matrixGroupNavRef, indicatorStyle: matrixGroupIndicatorStyle } = useNavPillsIndicator(matrixGroupMode);
  const { navRef: matrixMetricNavRef, indicatorStyle: matrixMetricIndicatorStyle } = useNavPillsIndicator(matrixMetricMode);
  const { navRef: matrixValueNavRef, indicatorStyle: matrixValueIndicatorStyle } = useNavPillsIndicator(matrixValueMode);
  const { navRef: matrixViewModeNavRef, indicatorStyle: matrixViewModeIndicatorStyle } = useNavPillsIndicator(matrixViewMode);
  const [matrixSortConfig, setMatrixSortConfig] = useState<SortConfig | null>(null);
  const [selectedMatrixOfficers, setSelectedMatrixOfficers] = useState<string[]>([]);
  const [selectedMatrixTribes, setSelectedMatrixTribes] = useState<string[]>([]);
  const [selectedMatrixGroups, setSelectedMatrixGroups] = useState<string[]>([]);
  const [pointStyleCache, setPointStyleCache] = useState<Record<string, HTMLCanvasElement>>({});

  const [trackerData, setTrackerData] = useState<any>()
  const [resultTrackerData, setResultTrackerData] = useState<any>()
  const [lastHourTrackerData, setLastHourTrackerData] = useState<any>()
  const [dailyPerformanceData, setDailyPerformanceData] = useState<MonitorDailyPerformanceResp[]>([])
  const [dailyResultMapData, setDailyResultMapData] = useState<MonitorDailyResultMapResp[]>([])
  const [dailyPerformanceViewMode, setDailyPerformanceViewMode] = useState<'summary' | 'map'>('summary')
  const [dailyPerformanceDate, setDailyPerformanceDate] = useState<string | null>(null)
  const [isLoadingDailyPerformance, setIsLoadingDailyPerformance] = useState<boolean>(false)
  const [popover, setPopover] = useState<{
    cluster: any
    x: number
    y: number
    pinned: boolean
  } | null>(null)
  const [userBadgePopover, setUserBadgePopover] = useState<{
    contributor: any;
    x: number;
    y: number;
  } | null>(null);

  const [isInsightExpanded, setIsInsightExpanded] = useState(false);
  const [areaBadgePopover, setAreaBadgePopover] = useState<{
    jobId: string;
    outstandingTarget: number;
    x: number;
    y: number;
  } | null>(null);

  const [firstLoad, setFirstLoad] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [areaPageSize, setAreaPageSize] = useState<number>(10);
  const [officerPageSize, setOfficerPageSize] = useState<number>(10);

  const monitorService = MonitorService()

  const [areaSearch, setAreaSearch] = useState<string>("")
  const [areaSortConfig, setAreaSortConfig] = useState<SortConfig | null>({ key: "Result", direction: "desc" })
  const [timeRange, setTimeRange] = useState<{ startDate: string | null, endDate: string | null }>({ startDate: null, endDate: null });

  const [officerSearch, setOfficerSearch] = useState<string>("")
  const [officerSortConfig, setOfficerSortConfig] = useState<SortConfig | null>({ key: "Result", direction: "desc" })

  const [selectedDateRangeKey, setSelectedDateRangeKey] = useState<string>("all")

  const [areaCurrentPage, setAreaCurrentPage] = useState<number>(1);
  const [officerCurrentPage, setOfficerCurrentPage] = useState<number>(1);

  const [autoRefresh, setAutoRefresh] = useState<number>(
    Number(localStorage.getItem("monitorAutoRefresh") || 0)
  );
  const [lastRefresh, setLastRefresh] = useState<string>("");

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOfficerId, setSelectedOfficerId] = useState<string | null>(null);

  const [showResultDetailModal, setShowResultDetailModal] = useState(false);
  const [selectedResultItem, setSelectedResultItem] = useState<any>(null);

  const [showAreaDetail, setShowAreaDetail] = useState(false)
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null)
  const [selectedAreaName, setSelectedAreaName] = useState<string>("")
  const [selectedAreaDescription, setSelectedAreaDescription] = useState<string>("")
  const [areaDetailData, setAreaDetailData] = useState<MonitorByAreaResponse | null>(null)
  const [selectedMenuOfficer, setSelectedMenuOfficer] = useState<"user" | "tribe">("user")
  const [isLegacyView, setIsLegacyView] = useState<boolean>(false)
  const [loadingAreaDetail, setLoadingAreaDetail] = useState(false)

  const [showEndProjectModal, setShowEndProjectModal] = useState<boolean>(false);
  const [confirmProjectNameInput, setConfirmProjectNameInput] = useState<string>("");
  const [isEndingProject, setIsEndingProject] = useState<boolean>(false);
  const [endProjectError, setEndProjectError] = useState<string | null>(null);

  const selectedProjectObj = useMemo(() => {
    return projectList.find(p => p.id === selectedProject);
  }, [projectList, selectedProject]);

  const handleOpenEndProjectModal = () => {
    setConfirmProjectNameInput("");
    setEndProjectError(null);
    setShowEndProjectModal(true);
  };

  const handleCloseEndProjectModal = () => {
    if (isEndingProject) return;
    setShowEndProjectModal(false);
    setConfirmProjectNameInput("");
    setEndProjectError(null);
  };

  const handleConfirmEndProject = async () => {
    if (!selectedProject || !selectedProjectObj) return;
    if (confirmProjectNameInput.trim() !== selectedProjectObj.name.trim()) {
      setEndProjectError("Nama project tidak sesuai.");
      return;
    }

    const token = profile?.data?.token;
    if (!token) {
      setEndProjectError("Token tidak ditemukan. Silakan login kembali.");
      return;
    }

    setIsEndingProject(true);
    setEndProjectError(null);
    try {
      await monitorService.endProject(token, selectedProject);
      setShowEndProjectModal(false);
      setConfirmProjectNameInput("");
      fetchData();
      fetchConfig();
    } catch (err: any) {
      console.error("Failed to end project:", err);
      setEndProjectError(typeof err === 'string' ? err : err?.message || "Gagal menghentikan project");
    } finally {
      setIsEndingProject(false);
    }
  };

  const handleBackToOverview = () => {
    navigate('/monitor/overview');
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    fetchData();
  }, [timeRange, selectedProject]);

  useEffect(() => {
    if (!isInitialLoad && selectedProject && selectedProject !== urlProjectId) {
      navigate(`/monitor/${selectedProject}`, { replace: true });
    }
  }, [selectedProject, urlProjectId, navigate, isInitialLoad]);

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await monitorService.dashboard(
        token,
        selectedProject,
        timeRange.startDate != null && timeRange.startDate !== "" ? timeRange.startDate : null,
        timeRange.endDate != null && timeRange.endDate !== "" ? timeRange.endDate : null
      );
      setMonitorData(res.data!);

      const labels = res.data?.progressTracker?.map((t) => t.date) ?? [];

      const lineLabelsMap = new Map();
      const barLabelsMap = new Map()

      res.data?.progressTracker.forEach((pt, index) => {
        pt.lineTracker.forEach((lt) => {
          if (!lineLabelsMap.has(lt.label)) {
            lineLabelsMap.set(lt.label, {
              type: "line",
              label: lt.label,
              borderColor: lt.color,
              data: [],
              borderWidth: 2,
              fill: false,
              yAxisID: "yRight",
              pointRadius: 1,
              pointHoverRadius: 5,
              hidden: lt.hidden,
            });
          }
          lineLabelsMap.get(lt.label).data.push(lt.value);
        });

        pt.barTracker.forEach((bt) => {
          if (!barLabelsMap.has(bt.label)) {
            barLabelsMap.set(bt.label, {
              type: "bar",
              label: bt.label,
              backgroundColor: bt.color,
              data: [],
              yAxisID: "yLeft",
              hidden: bt.hidden,
            });
          }
          barLabelsMap.get(bt.label).data.push(bt.value);
        });
      });

      const datasets = [...lineLabelsMap.values(), ...barLabelsMap.values()];

      setTrackerData({
        labels,
        datasets,
      });

      if (!dailyPerformanceDate) {
        setDailyPerformanceData(res.data?.dailyPerformance ?? []);
        if (res.data?.resultTracker) {
          setResultTrackerData(buildResultTrackerChartData(res.data.resultTracker));
        }
      }
      setDailyResultMapData(res.data?.dailyResultMap ?? [])

      const now = new Date();
      const formatted = now.toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      setLastRefresh(`${formatted.replace(",", "")}`);

    } catch (err) {
      console.error(err);
      window.alert('Terjadi kesalahan saat mengambil data monitor');
    } finally {
      setLoading(false);
      if (!firstLoad) setFirstLoad(true);
    }
  };

  const fetchConfig = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    try {
      const res = await monitorService.monitorConfig(token);
      const projects = res.data?.projectList ?? [];
      setProjectList(projects);

      if (urlProjectId && projects.some(p => p.id === urlProjectId)) {
        setSelectedProject(urlProjectId);
      } else if (projects.length > 0) {
        setSelectedProject(projects[0].id);
        navigate(`/monitor/${projects[0].id}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsInitialLoad(false);
    }
  };

  const fetchDailyPerformance = async (date: string) => {
    const token = profile?.data.token;
    if (!token || !selectedProject) return;

    setIsLoadingDailyPerformance(true);
    try {
      const res = await monitorService.dailyPerformance(
        token,
        selectedProject,
        date,
        date
      );
      const dailyPerfs = res.data?.dailyPerformances ?? (res.data as any)?.dailyPerformance ?? [];
      setDailyPerformanceData(dailyPerfs);

      if (res.data?.resultTracker) {
        setResultTrackerData(buildResultTrackerChartData(res.data.resultTracker));
      }

      if (res.data?.dailyResultMap) {
        setDailyResultMapData(res.data.dailyResultMap);
      }
    } catch (err) {
      console.error('Error fetching daily performance:', err);
    } finally {
      setIsLoadingDailyPerformance(false);
    }
  };

  const dailyDatePickerRef = useRef<HTMLInputElement>(null);

  const activeDailyDate = useMemo(() => {
    if (dailyPerformanceDate) {
      return parseDailyYMD(dailyPerformanceDate);
    }
    if (timeRange.startDate && timeRange.startDate.trim() !== '') {
      return parseDailyYMD(timeRange.startDate);
    }
    return new Date();
  }, [dailyPerformanceDate, timeRange.startDate]);

  const handlePrevDailyDate = () => {
    const prev = new Date(activeDailyDate);
    prev.setDate(prev.getDate() - 1);
    const ymd = formatDailyDateYMD(prev);
    setDailyPerformanceDate(ymd);
    fetchDailyPerformance(ymd);
  };

  const handleNextDailyDate = () => {
    const next = new Date(activeDailyDate);
    next.setDate(next.getDate() + 1);
    const ymd = formatDailyDateYMD(next);
    setDailyPerformanceDate(ymd);
    fetchDailyPerformance(ymd);
  };

  const handleResetDailyDate = () => {
    setDailyPerformanceDate(null);
    setDailyPerformanceData(monitorData?.dailyPerformance ?? []);
    if (monitorData?.resultTracker) {
      setResultTrackerData(buildResultTrackerChartData(monitorData.resultTracker));
    }
  };

  const handleProjectChange = (p: any) => {
    const newProjectId = p ? p.id : "";
    setSelectedProject(newProjectId);
    setSelectedMenuOfficer("user");
    if (newProjectId) {
      navigate(`/monitor/${newProjectId}`);
    }
  };

  const handleViewOfficerDetail = (userId: string) => {
    setSelectedOfficerId(userId);
    setShowDetailModal(true);
  };

  const handleOpenResultDetail = (item: any) => {
    if (item && item.id) {
      setSelectedResultItem({ id: item.id });
      setShowResultDetailModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedOfficerId(null);
  };

  useEffect(() => {
    localStorage.setItem("monitorAutoRefresh", String(autoRefresh));
  }, [autoRefresh]);

  useEffect(() => {
    if (!monitorData?.officerDailyMatrix) return;

    const loadPointStyles = async () => {
      const cache: Record<string, HTMLCanvasElement> = {};
      const promises = monitorData.officerDailyMatrix.map(async (row, idx) => {
        const canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 24;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.save();
        ctx.beginPath();
        ctx.arc(12, 12, 11, 0, Math.PI * 2);
        ctx.clip();

        const color = LINE_COLORS[idx % LINE_COLORS.length];

        if (row.profilePicture) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = row.profilePicture;
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            ctx.drawImage(img, 0, 0, 24, 24);
          } catch (e) {
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, 24, 24);
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const initials = row.userName.trim().split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase();
            ctx.fillText(initials, 12, 12);
          }
        } else {
          ctx.fillStyle = color;
          ctx.fillRect(0, 0, 24, 24);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 8px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const initials = row.userName.trim().split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase();
          ctx.fillText(initials, 12, 12);
        }
        ctx.restore();

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(12, 12, 11, 0, Math.PI * 2);
        ctx.stroke();

        cache[row.userId] = canvas;
      });

      await Promise.all(promises);
      setPointStyleCache(cache);
    };

    loadPointStyles();
  }, [monitorData]);

  useEffect(() => {
    if (monitorData?.officerDailyMatrix && selectedMatrixOfficers.length > 0) {
      const newIds = monitorData.officerDailyMatrix.map(o => o.userId);
      setSelectedMatrixOfficers(prev => prev.filter(id => newIds.includes(id)));
    }
  }, [monitorData]);

  useEffect(() => {
    if (monitorData?.officerDailyMatrix && selectedMatrixTribes.length > 0) {
      const newTribes = Array.from(new Set(monitorData.officerDailyMatrix.map(o => o.tribeName || 'No Tribe')));
      setSelectedMatrixTribes(prev => prev.filter(t => newTribes.includes(t)));
    }
  }, [monitorData]);

  useEffect(() => {
    if (monitorData?.officerDailyMatrix && selectedMatrixGroups.length > 0) {
      const allGroups: string[] = [];
      monitorData.officerDailyMatrix.forEach((o: any) => {
        if (o.groups && o.groups.length > 0) {
          o.groups.forEach((g: any) => {
            if (g.groupName) allGroups.push(g.groupName);
          });
        } else if (o.groupName) {
          allGroups.push(o.groupName);
        } else {
          allGroups.push('No Group');
        }
      });
      const newGroups = Array.from(new Set(allGroups));
      setSelectedMatrixGroups(prev => prev.filter(g => newGroups.includes(g)));
    } else if (!monitorData?.officerDailyMatrix) {
      setSelectedMatrixGroups([]);
    }
  }, [monitorData]);

  useEffect(() => {
    if (!autoRefresh || !firstLoad) return;
    const interval = setInterval(fetchData, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, token, selectedProject, timeRange]);

  const today = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, "0")
    const day = String(today.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      yLeft: {
        type: "linear",
        position: "left",
        beginAtZero: true,
        title: {
          display: true,
          text: "Total Akuisisi per Hari",
        },
        grid: {
          drawOnChartArea: false
        },
      },
      yRight: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: {
          display: true,
          text: "Progress Keseluruhan",
        },
      },
    },
  };

  const handleAreaSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (areaSortConfig && areaSortConfig.key === key) {
      direction = areaSortConfig.direction === 'desc' ? 'asc' : 'desc';
    }
    setAreaSortConfig({ key, direction });
    setAreaCurrentPage(1);
  };

  const handleOfficerSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (officerSortConfig && officerSortConfig.key === key) {
      direction = officerSortConfig.direction === 'desc' ? 'asc' : 'desc';
    }
    setOfficerSortConfig({ key, direction });
    setOfficerCurrentPage(1);
  };

  const handleMatrixSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (matrixSortConfig && matrixSortConfig.key === key) {
      direction = matrixSortConfig.direction === 'desc' ? 'asc' : 'desc';
    }
    setMatrixSortConfig({ key, direction });
  };

  const sortedAreas = (monitorData?.jobList ?? [])
    .filter((job) => {
      const term = areaSearch.toLowerCase();
      const matchName = job.name?.toLowerCase().includes(term);
      const matchDesc = (job.description || job.groupDescription)?.toLowerCase().includes(term);
      return matchName || Boolean(matchDesc);
    })
    .sort((a, b) => {
      if (!areaSortConfig) return 0;
      const { key, direction } = areaSortConfig;

      if (key === "name") {
        const valA = a.name.toLowerCase();
        const valB = b.name.toLowerCase();
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      }

      if (key === "target") {
        const valA = Number(a.target ?? 0);
        const valB = Number(b.target ?? 0);
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      }

      const resultA = a.results?.find((r) => r.label === key || r.label?.toLowerCase() === key.toLowerCase());
      const resultB = b.results?.find((r) => r.label === key || r.label?.toLowerCase() === key.toLowerCase());

      if (!resultA && !resultB) return 0;
      if (!resultA) return direction === "asc" ? -1 : 1;
      if (!resultB) return direction === "asc" ? 1 : -1;

      const rawA = resultA.value ?? "";
      const rawB = resultB.value ?? "";
      const numA = Number(rawA);
      const numB = Number(rawB);
      const isNumericA = rawA !== "" && !isNaN(numA);
      const isNumericB = rawB !== "" && !isNaN(numB);

      if (isNumericA && isNumericB) {
        return direction === "asc" ? numA - numB : numB - numA;
      }

      const valueA = String(rawA).toLowerCase();
      const valueB = String(rawB).toLowerCase();
      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });

  const hasAreaResultColumns = (sortedAreas?.[0]?.results?.length ?? 0) > 0;

  const sortedOfficers = (
    (selectedMenuOfficer === "user"
      ? monitorData?.contributors
      : monitorData?.tribes) ?? []
  )
    .filter((c) =>
      c.name.toLowerCase().includes(officerSearch.toLowerCase())
    )
    .sort((a, b) => {
      if (!officerSortConfig) return 0;
      const { key, direction } = officerSortConfig;

      if (key === "name") {
        const valA = a.name.toLowerCase();
        const valB = b.name.toLowerCase();
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      }

      const resultA = a.results?.find((r) => r.label === key || r.label?.toLowerCase() === key.toLowerCase());
      const resultB = b.results?.find((r) => r.label === key || r.label?.toLowerCase() === key.toLowerCase());

      if (!resultA && !resultB) return 0;
      if (!resultA) return direction === "asc" ? -1 : 1;
      if (!resultB) return direction === "asc" ? 1 : -1;

      const rawA = resultA.value ?? "";
      const rawB = resultB.value ?? "";
      const numA = Number(rawA);
      const numB = Number(rawB);
      const isNumericA = !isNaN(numA) && rawA !== "";
      const isNumericB = !isNaN(numB) && rawB !== "";

      if (isNumericA && isNumericB) {
        return direction === "asc" ? numA - numB : numB - numA;
      }

      const valueA = String(rawA).toLowerCase();
      const valueB = String(rawB).toLowerCase();
      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });

  const paginatedAreas = sortedAreas?.slice(
    (areaCurrentPage - 1) * areaPageSize,
    areaCurrentPage * areaPageSize
  );

  const totalAreaPages = sortedAreas ? Math.ceil(sortedAreas.length / areaPageSize) : 1;

  const handleAreaPageChange = (page: number) => {
    if (page < 1 || page > totalAreaPages) return;
    setAreaCurrentPage(page);
  };

  const handleOpenAreaDetail = async (areaId: string, areaName: string, areaDescription: string) => {
    setSelectedAreaId(areaId)
    setSelectedAreaName(areaName)
    setSelectedAreaDescription(areaDescription)
    setAreaDetailData(null)
    setShowAreaDetail(true)
    setLoadingAreaDetail(true)

    try {
      const res = await monitorService.monitorByArea(
        token,
        selectedProject,
        areaId,
        areaName,
        timeRange.startDate || null,
        timeRange.endDate || null
      )
      setAreaDetailData(res.data || null)
    } catch (err) {
      console.error(err);
      window.alert('Terjadi kesalahan saat mengambil detail area');
    } finally {
      setLoadingAreaDetail(false)
    }
  }

  const paginatedOfficers = sortedOfficers?.slice(
    (officerCurrentPage - 1) * officerPageSize,
    officerCurrentPage * officerPageSize
  );

  const totalOfficerPages = sortedOfficers ? Math.ceil(sortedOfficers.length / officerPageSize) : 1;

  const handleOfficerPageChange = (page: number) => {
    if (page < 1 || page > totalOfficerPages) return;
    setOfficerCurrentPage(page);
  };

  const renderSortIndicator = (key: string, type: 'area' | 'officer' | 'matrix') => {
    const config = type === 'area' ? areaSortConfig : type === 'officer' ? officerSortConfig : matrixSortConfig;
    if (config?.key === key) {
      return config.direction === 'asc' ? '↑' : '↓';
    }
    return '↕';
  };

  const handleCopyAreaToClipboard = async () => {
    if (!sortedAreas || sortedAreas.length === 0) return;
    try {
      const hasDescription = sortedAreas.some(a => a.description || a.groupDescription);
      const headers = ['Name'];
      if (hasDescription) {
        headers.push('Description');
      }
      if (selectedDateRangeKey === "all" && hasAreaResultColumns) {
        headers.push('Target');
      }
      const resultHeaders = [...(sortedAreas[0].results || [])]
        .sort((a, b) => a.sort - b.sort)
        .map(r => r.label);
      headers.push(...resultHeaders);

      const rows = sortedAreas.map(area => {
        const row = [area.name || '-'];
        if (hasDescription) {
          row.push(area.description || area.groupDescription || '-');
        }
        if (selectedDateRangeKey === "all" && hasAreaResultColumns) {
          row.push((area.target ?? 0).toString());
        }
        const resultValues = [...(area.results || [])]
          .sort((a, b) => a.sort - b.sort)
          .map(r => r.value?.toString() || '0');
        row.push(...resultValues);
        return row.join('\t');
      });

      const tsv = [headers.join('\t'), ...rows].join('\n');
      await navigator.clipboard.writeText(tsv);
      alert('Data By Area berhasil disalin ke clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Gagal menyalin data.');
    }
  };

  const handleCopyOfficerToClipboard = async () => {
    if (!sortedOfficers || sortedOfficers.length === 0) return;
    try {
      const headers = ['Name'];
      const resultHeaders = [...(sortedOfficers[0].results || [])]
        .sort((a, b) => a.sort - b.sort)
        .map(r => r.label);
      headers.push(...resultHeaders);

      const rows = sortedOfficers.map(officer => {
        const row = [officer.name || '-'];
        const resultValues = [...(officer.results || [])]
          .sort((a, b) => a.sort - b.sort)
          .map(r => r.value?.toString() || '0');
        row.push(...resultValues);
        return row.join('\t');
      });

      const tsv = [headers.join('\t'), ...rows].join('\n');
      await navigator.clipboard.writeText(tsv);
      alert('Data By Officer berhasil disalin ke clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Gagal menyalin data.');
    }
  };

  const PerformanceBar = ({ data, hideTitle = false }: { data: any[]; hideTitle?: boolean }) => {
    const maxScore = 100
    const clusters = groupByValue(data).sort((a, b) => a.score - b.score)

    const handleMouseEnter = (e: any, cluster: any) => {
      if (popover?.pinned) return
      const rect = e.currentTarget.getBoundingClientRect()
      let x = rect.left + rect.width / 2
      const y = rect.top
      const tooltipWidth = 240
      const padding = 16
      if (x - tooltipWidth / 2 < padding) {
        x = tooltipWidth / 2 + padding
      }
      if (x + tooltipWidth / 2 > window.innerWidth - padding) {
        x = window.innerWidth - tooltipWidth / 2 - padding
      }
      setPopover({ cluster, x, y, pinned: false })
    }

    const handleMouseLeave = () => {
      if (popover?.pinned) return
      setPopover(null)
    }

    const handleClick = (e: any, cluster: any) => {
      const rect = e.currentTarget.getBoundingClientRect()
      let x = rect.left + rect.width / 2
      const y = rect.top
      const tooltipWidth = 240
      const padding = 16
      if (x - tooltipWidth / 2 < padding) {
        x = tooltipWidth / 2 + padding
      }
      if (x + tooltipWidth / 2 > window.innerWidth - padding) {
        x = window.innerWidth - tooltipWidth / 2 - padding
      }
      setPopover({ cluster, x, y, pinned: true })
    }

    return (
      <div style={{ marginBottom: 30 }}>
        {!hideTitle && (
          <div className="d-flex align-items-center mb-3">
            <h5 className="mb-0 fw-semibold">Daily Performance</h5>
          </div>
        )}
        <div style={{ position: "relative", paddingTop: 28 }}>
          {clusters.map((cluster, index) => {
            const percent = (cluster.score / maxScore) * 100
            const firstUser = cluster.users[0]
            const count = cluster.users.length
            return (
              <div
                key={cluster.score}
                style={{
                  position: "absolute",
                  left: `${percent}%`,
                  top: 0,
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => handleMouseEnter(e, cluster)}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => handleClick(e, cluster)}
              >
                {count === 1 ? (
                  firstUser.profilePictureUrl ? (
                    <img
                      src={firstUser.profilePictureUrl}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: index === clusters.length - 1 ? "3px solid #f5c518" : "2px solid white",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: index === clusters.length - 1 ? "3px solid #f5c518" : "2px solid white",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                        background: "#5b8def",
                        color: "white",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {getInitials(firstUser.name)}
                    </div>
                  )
                ) : (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      border: index === clusters.length - 1 ? "3px solid #f5c518" : "2px solid white",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                      background: "#2a6bdc",
                      color: "white",
                      fontSize: 12,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {count}
                  </div>
                )}
              </div>
            )
          })}
          <div
            style={{
              height: 10,
              borderRadius: 20,
              background: "linear-gradient(90deg,#e9eef5 0%,#5b8def 50%,#2a6bdc 100%)"
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 10,
            fontSize: 14,
            fontWeight: 500,
            color: "#555"
          }}
        >
          <span>Least Performer</span>
          <span>Top Performer</span>
        </div>
        {popover && (
          <div
            style={{
              position: "fixed",
              left: popover.x,
              top: popover.y - 10,
              transform: "translate(-50%, -100%)",
              background: "white",
              borderRadius: 10,
              boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
              padding: 14,
              zIndex: 2000,
              width: 240
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Users ({popover.cluster.users.length})
            </div>
            {popover.cluster.users.map((user: any) => (
              <div
                key={user.userId}
                className="cursor-pointer text-decoration-none text-primary"
                onClick={() => { setPopover(null); handleViewOfficerDetail(user.userId) }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 4px",
                  cursor: "pointer"
                }}
              >
                <span style={{ color: "" }}>{user.name}</span>
                <span style={{ fontWeight: 600 }}>{user.value}</span>
              </div>
            ))}
            {popover.pinned && (
              <div
                style={{
                  marginTop: 8,
                  textAlign: "right",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "#666"
                }}
                onClick={() => setPopover(null)}
              >
                Close
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const InsightSection = () => {
    const insight = monitorData?.insightData;
    if (!insight || !insight.insightContent) return null;

    const renderFormattedText = (text: string) => {
      const formatted = text.replace(/\*\*(.*?)\*\*/g, '<b class="text-dark">$1</b>');
      return { __html: formatted };
    };

    const getTeaser = (text: string) => {
      const cleanText = text.replace(/\*\*/g, '').replace(/📊|🔍|🎯/g, '');
      return cleanText.length > 250 ? cleanText.substring(0, 247) + "..." : cleanText;
    };

    return (
      <div className="col-12 mb-3">
        <div
          className="card border-0 shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)',
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            borderLeft: '6px solid #0d6efd'
          }}
        >
          <div
            className="card-header bg-transparent border-0 p-3 cursor-pointer"
            onClick={() => setIsInsightExpanded(!isInsightExpanded)}
          >
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="d-flex align-items-center">
                <div
                  className="me-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '42px', height: '42px',
                    backgroundColor: '#e7f0ff', borderRadius: '12px', color: '#0d6efd'
                  }}
                >
                  <i className="fa-solid fa-wand-magic-sparkles fs-5"></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold text-primary d-flex align-items-center">
                    Project Insight
                    <span className="badge bg-primary-subtle text-primary ms-2" style={{ fontSize: '10px' }}>Mora AI</span>
                  </h6>
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    <i className="fa-regular fa-clock me-1"></i>
                    Last updated: {insight.lastUpdated || '-'}
                  </small>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <button className="btn btn-sm btn-outline-primary rounded-pill px-3 me-2" style={{ fontSize: '11px', fontWeight: 600 }}>
                  {isInsightExpanded ? 'Show Less' : 'View Full Report'}
                </button>
                <i className={`fa-solid fa-chevron-${isInsightExpanded ? 'up' : 'down'} text-muted`}></i>
              </div>
            </div>
            {!isInsightExpanded && (
              <div
                className="mt-2 ps-1 animate__animated animate__fadeIn"
                style={{
                  fontSize: '13.5px',
                  color: '#6c757d',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                <i className="fa-solid fa-quote-left me-2 opacity-25"></i>
                {getTeaser(insight.insightContent)}
              </div>
            )}
          </div>
          <div className={`collapse ${isInsightExpanded ? 'show' : ''}`}>
            <div className="card-body pt-0 px-3 pb-3">
              <hr className="mt-1 mb-3 opacity-10" />
              <div
                className="insight-content ps-2 border-start ms-2"
                style={{
                  fontSize: '14.5px',
                  lineHeight: '1.8',
                  color: '#444',
                  whiteSpace: 'pre-line'
                }}
                dangerouslySetInnerHTML={renderFormattedText(insight.insightContent)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getInitials = (name: string) => {
    if (!name) return "?"
    const parts = name.trim().split(/\s+/)
    if (parts.length === 0 || !parts[0]) return "?"
    if (parts.length === 1) {
      return parts[0][0]?.toUpperCase() || "?"
    }
    return (
      (parts[0][0]?.toUpperCase() || "") +
      (parts[1][0]?.toUpperCase() || "")
    )
  }

  const groupByValue = (data: any[]) => {
    const map: Record<number, any[]> = {}
    data.forEach(u => {
      if (!map[u.score]) map[u.score] = []
      map[u.score].push(u)
    })
    return Object.entries(map).map(([score, users]) => ({
      score: Number(score),
      users
    }))
  }

  const handleUserBadgeMouseEnter = (e: React.MouseEvent, contributor: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    let x = rect.left + rect.width / 2;
    const y = rect.top;
    const tooltipWidth = 240;
    const padding = 16;
    if (x - tooltipWidth / 2 < padding) {
      x = tooltipWidth / 2 + padding;
    }
    if (x + tooltipWidth / 2 > window.innerWidth - padding) {
      x = window.innerWidth - tooltipWidth / 2 - padding;
    }
    setUserBadgePopover({ contributor, x, y });
  };

  const handleUserBadgeMouseLeave = () => {
    setUserBadgePopover(null);
  };

  const tribeDailyMatrix = useMemo(() => {
    if (!monitorData?.officerDailyMatrix) return [];
    const tribeMap: Record<string, Record<string, { count: number; percentage: number }>> = {};
    const dates = monitorData.officerDailyMatrix[0]?.days.map(d => d.date) ?? [];
    monitorData.officerDailyMatrix.forEach((row) => {
      const tName = row.tribeName || 'No Tribe';
      if (!tribeMap[tName]) {
        tribeMap[tName] = {};
        dates.forEach(d => {
          tribeMap[tName][d] = { count: 0, percentage: 0 };
        });
      }
      row.days.forEach(d => {
        if (!tribeMap[tName][d.date]) {
          tribeMap[tName][d.date] = { count: 0, percentage: 0 };
        }
        tribeMap[tName][d.date].count += d.count;
        tribeMap[tName][d.date].percentage += (d.percentage || 0);
      });
    });
    return Object.entries(tribeMap).map(([name, datesMap]) => ({
      tribeName: name,
      days: Object.entries(datesMap).map(([date, val]) => ({
        date,
        count: val.count,
        percentage: Number(val.percentage.toFixed(4)),
      }))
    }));
  }, [monitorData]);

  const matrixDates = useMemo<string[]>(() => {
    return monitorData?.officerDailyMatrix?.[0]?.days?.map((d) => d.date) ?? [];
  }, [monitorData]);

  const groupDailyMatrix = useMemo(() => {
    if (!monitorData?.officerDailyMatrix) return [];
    const groupMap: Record<string, Record<string, { count: number; percentage: number }>> = {};
    const groupTargets: Record<string, number> = {};
    const dates = monitorData.officerDailyMatrix[0]?.days.map(d => d.date) ?? [];

    monitorData.officerDailyMatrix.forEach((row: any) => {
      if (row.groups && row.groups.length > 0) {
        row.groups.forEach((grp: any) => {
          const gName = grp.groupName || 'No Group';
          if (!groupMap[gName]) {
            groupMap[gName] = {};
            dates.forEach(d => {
              groupMap[gName][d] = { count: 0, percentage: 0 };
            });
            groupTargets[gName] = grp.target || 0;
          }
          grp.days?.forEach((d: any) => {
            if (!groupMap[gName][d.date]) {
              groupMap[gName][d.date] = { count: 0, percentage: 0 };
            }
            groupMap[gName][d.date].count += d.count;
            groupMap[gName][d.date].percentage += (d.percentage || 0);
          });
        });
      } else {
        const gName = row.groupName || 'No Group';
        if (!groupMap[gName]) {
          groupMap[gName] = {};
          dates.forEach(d => {
            groupMap[gName][d] = { count: 0, percentage: 0 };
          });
          groupTargets[gName] = row.target || 0;
        }
        row.days?.forEach((d: any) => {
          if (!groupMap[gName][d.date]) {
            groupMap[gName][d.date] = { count: 0, percentage: 0 };
          }
          groupMap[gName][d.date].count += d.count;
          groupMap[gName][d.date].percentage += (d.percentage || 0);
        });
      }
    });

    return Object.entries(groupMap).map(([name, datesMap]) => ({
      groupName: name,
      target: groupTargets[name] || 0,
      days: Object.entries(datesMap).map(([date, val]) => ({
        date,
        count: val.count,
        percentage: Number(val.percentage.toFixed(4)),
      }))
    }));
  }, [monitorData]);

  const groupedSourceRows = useMemo<any[]>(() => {
    if (matrixGroupMode === 'officer') {
      return monitorData?.officerDailyMatrix ?? [];
    } else if (matrixGroupMode === 'tribe') {
      return tribeDailyMatrix;
    } else {
      return groupDailyMatrix;
    }
  }, [matrixGroupMode, monitorData, tribeDailyMatrix, groupDailyMatrix]);

  const activeSelections = matrixGroupMode === 'officer'
    ? selectedMatrixOfficers
    : matrixGroupMode === 'tribe'
      ? selectedMatrixTribes
      : selectedMatrixGroups;

  const filteredMatrixRows = useMemo(() => {
    return groupedSourceRows.filter((row: any) => {
      const key = matrixGroupMode === 'officer'
        ? row.userId
        : matrixGroupMode === 'tribe'
          ? row.tribeName
          : row.groupName;
      return activeSelections.length === 0 || activeSelections.includes(key);
    });
  }, [groupedSourceRows, activeSelections, matrixGroupMode]);

  const dropdownOptions = useMemo(() => {
    const mapped = groupedSourceRows.map((row: any) => {
      const code = matrixGroupMode === 'officer'
        ? row.userId
        : matrixGroupMode === 'tribe'
          ? row.tribeName
          : row.groupName;
      const name = matrixGroupMode === 'officer'
        ? row.userName
        : matrixGroupMode === 'tribe'
          ? row.tribeName
          : row.groupName;
      const showTribe = Boolean(row.tribeName && row.tribeName.trim().toLowerCase() !== 'no tribe');
      const subLabel = matrixGroupMode === 'officer'
        ? (showTribe ? row.tribeName : undefined)
        : undefined;
      return { code, name, tribeName: row.tribeName, groupName: row.groupName, subLabel };
    });
    const seen = new Set<string>();
    return mapped.filter(opt => {
      if (seen.has(opt.code)) return false;
      seen.add(opt.code);
      return true;
    });
  }, [groupedSourceRows, matrixGroupMode]);

  const sortedMatrixRows = useMemo(() => {
    return [...filteredMatrixRows].sort((a: any, b: any) => {
      if (!matrixSortConfig) return 0;
      const { key, direction } = matrixSortConfig;
      const multiplier = direction === 'asc' ? 1 : -1;

      if (key === 'name') {
        const nameA = matrixGroupMode === 'officer' ? a.userName : matrixGroupMode === 'tribe' ? a.tribeName : a.groupName;
        const nameB = matrixGroupMode === 'officer' ? b.userName : matrixGroupMode === 'tribe' ? b.tribeName : b.groupName;
        return (nameA || '').localeCompare(nameB || '') * multiplier;
      }
      if (key === 'tribe') {
        const tribeA = a.tribeName || '';
        const tribeB = b.tribeName || '';
        return tribeA.localeCompare(tribeB) * multiplier;
      }
      if (key === 'total') {
        const getRowTotalVal = (item: any) => {
          const totalCount = item.days.reduce((sum: number, d: any) => sum + (d.count || 0), 0);
          if (matrixMetricMode === 'percentage') {
            if (item.target && item.target > 0) {
              return totalCount / item.target;
            }
            const pctSum = item.days.reduce((sum: number, d: any) => sum + (d.percentage || 0), 0);
            return Math.abs(pctSum - 1.0) <= 0.0002 ? 1.0 : pctSum;
          }
          return totalCount;
        };
        return (getRowTotalVal(a) - getRowTotalVal(b)) * multiplier;
      }
      const dayA = a.days.find((d: any) => d.date === key);
      const dayB = b.days.find((d: any) => d.date === key);
      const valA = matrixMetricMode === 'percentage' ? (dayA?.percentage ?? 0) : (dayA?.count ?? 0);
      const valB = matrixMetricMode === 'percentage' ? (dayB?.percentage ?? 0) : (dayB?.count ?? 0);
      return (valA - valB) * multiplier;
    });
  }, [filteredMatrixRows, matrixSortConfig, matrixGroupMode, matrixMetricMode]);

  const matrixChartData = {
    labels: matrixDates,
    datasets: filteredMatrixRows.map((row: any, idx: number) => {
      const color = LINE_COLORS[idx % LINE_COLORS.length];
      let runningSum = 0;
      const dataPoints = row.days.map((d: any) => {
        if (matrixMetricMode === 'percentage') {
          if (matrixValueMode === 'cumulative') {
            runningSum += d.count;
            if (row.target && row.target > 0) {
              return Number(((runningSum / row.target) * 100).toFixed(2));
            }
            const rawCum = row.days.slice(0, row.days.indexOf(d) + 1).reduce((sum: number, day: any) => sum + (day.percentage || 0), 0);
            const clamped = Math.abs(rawCum - 1.0) <= 0.0002 ? 1.0 : rawCum;
            return Number((clamped * 100).toFixed(2));
          }
          return Number(((d.percentage || 0) * 100).toFixed(2));
        }
        if (matrixValueMode === 'cumulative') {
          runningSum += d.count;
          return runningSum;
        }
        return d.count;
      });
      const daysCount = row.days.length;
      const avatarCanvas = matrixGroupMode === 'officer' ? pointStyleCache[row.userId] : undefined;
      const label = matrixGroupMode === 'officer'
        ? (row.groupName ? `${row.userName} (${row.groupName})` : row.userName)
        : matrixGroupMode === 'tribe'
          ? row.tribeName
          : row.groupName;
      return {
        label,
        data: dataPoints,
        borderColor: color,
        backgroundColor: color + '1a',
        tension: 0.3,
        borderWidth: 2,
        pointStyle: (context: any) => {
          if (matrixGroupMode === 'officer' && (context.index === undefined || context.index === daysCount - 1)) {
            return avatarCanvas || 'circle';
          }
          return 'circle';
        },
        pointRadius: (context: any) => {
          if (matrixGroupMode === 'officer' && (context.index === undefined || context.index === daysCount - 1)) {
            return avatarCanvas ? 12 : 3;
          }
          return 3;
        },
        pointHoverStyle: (matrixGroupMode === 'officer' && avatarCanvas) || 'circle',
        pointHoverRadius: (matrixGroupMode === 'officer' && avatarCanvas) ? 14 : 5,
        fill: false,
      };
    }) ?? [],
  };

  const matrixChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          font: { size: 10, weight: 'bold' },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        itemSort: (a: any, b: any) => b.raw - a.raw,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.raw;
            return matrixMetricMode === 'percentage'
              ? `${label}: ${val}%`
              : `${label}: ${val} items`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 9 } },
      },
      y: {
        border: { dash: [4, 4] },
        grid: { color: '#f1f5f9' },
        ticks: {
          font: { size: 9 },
          callback: (value: any) => matrixMetricMode === 'percentage' ? `${value}%` : value,
        },
        title: {
          display: true,
          text: matrixMetricMode === 'percentage'
            ? (matrixValueMode === 'cumulative' ? 'Cumulative Percentage' : 'Acquisition Percentage')
            : (matrixValueMode === 'cumulative' ? 'Cumulative Count' : 'Acquisition Count'),
          font: { size: 10, weight: 'bold' },
        },
      },
    },
  };

  return (
    <div className="page-wrapper position-relative">
      <style>{`
        .table-hover tbody tr:hover td {
          background-color: #f8fafc !important;
        }
      `}</style>
      {loading && <LoadingOverlay />}

      <div className="row g-3">
        <div className="col-12">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div className="gap-2 d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
              <span
                onClick={handleBackToOverview}
                style={{
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: '#64748b',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.15s ease',
                  padding: '4px 6px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#334155';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#64748b';
                }}
                title="Back to Overview"
              >
                <i className="fa-solid fa-arrow-left" style={{ fontSize: '0.85rem' }} />
              </span>
              <div style={{ width: 240 }}>
                <ProjectFilterDropdown
                  key={selectedProject}
                  projects={projectList}
                  defaultSelectedId={selectedProject}
                  allowAllProject={false}
                  onChange={handleProjectChange}
                />
              </div>
              <DateRangeFilter onChange={(v, key) => {
                setSelectedDateRangeKey(key)
                setTimeRange({ startDate: v.startDate, endDate: v.endDate })
                setDailyPerformanceDate(null)
              }} />
            </div>

            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
              <label className="mb-0 me-2 fw-semibold">Auto Refresh</label>
              <CustomDropdown
                value={autoRefresh}
                options={AUTO_REFRESH_OPTIONS}
                onChange={(opt) => setAutoRefresh(Number(opt.id))}
                buttonStyle={{ height: "50px", minWidth: "140px" }}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={fetchData}
                style={{ height: "50px" }}
                title="Refresh"
              >
                <i className="fa fa-rotate"></i>
              </button>
              {selectedProject && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/monitor/${selectedProject}/data`)}
                  style={{ height: "50px", whiteSpace: "nowrap" }}
                  title="Field Data Monitor"
                >
                  <i className="fa-solid fa-map-location-dot me-1"></i> Field Data Monitor
                </button>
              )}
              {hasProjectAccess(profile, 'update') && selectedProject && selectedProjectObj?.isRunning && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleOpenEndProjectModal}
                  style={{ height: "50px", whiteSpace: "nowrap" }}
                >
                  <i className="fa fa-stop-circle me-1"></i> End Project
                </button>
              )}
            </div>
          </div>

          <p className="text-end text-muted" style={{ fontSize: "0.85rem" }}>
            Last refresh at {lastRefresh || "-"}
          </p>
        </div>
      </div>

      {
        firstLoad && !loading &&

        <div className="row g-3" >
          <div className="col-12">
            <InsightSection />
          </div>
          {/* Left Column (Acquisition Rates & Status Breakdown) */}
          <div className="col-12 col-lg-4 d-flex flex-column gap-3">
            {/* Widget 1: Acquisition Rates */}
            <div className="p-3 border-0 shadow-sm w-100 bg-white" style={{ borderRadius: '16px' }}>
              <div className="d-flex flex-column h-100 justify-content-between">
                <div className="mb-3 d-flex justify-content-between align-items-start">
                  <div style={{ minWidth: 0 }}>
                    <h6 className="fw-bold mb-1 text-dark text-truncate" style={{ fontSize: '0.95rem' }}>
                      Performance Rates
                    </h6>
                    <p className="mb-0 text-muted text-wrap" style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                      Comparing last 60 minutes traffic vs baseline periods
                    </p>
                  </div>
                  {timeRange.startDate == null && timeRange.endDate == null && (
                    <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '0.65rem', fontWeight: 500 }}>
                      Today: {today()}
                    </span>
                  )}
                </div>

                <div className="row g-0 align-items-center flex-grow-1 mt-2">
                  {/* Today vs Yesterday */}
                  {(() => {
                    const todayVal = monitorData?.lastHourTracker?.todayLastHour ?? 0;
                    const compareVal = monitorData?.lastHourTracker?.yesterdayLastHour ?? 0;

                    const diff = todayVal - compareVal;
                    const percent = compareVal === 0 ? 0 : ((diff / compareVal) * 100);
                    const isUp = diff >= 0;

                    return (
                      <div className="col-6 pe-4 border-end d-flex flex-column justify-content-between" style={{ height: '95px' }}>
                        <div>
                          <div className="d-flex align-items-center mb-1" style={{ gap: '8px' }}>
                            <span className="text-primary" style={{ fontSize: '0.9rem' }}>
                              <i className="fa-solid fa-clock-rotate-left"></i>
                            </span>
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.75rem' }}>
                              Today vs Yesterday
                            </span>
                          </div>

                          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <div>
                              <span className="text-muted d-block" style={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: 500 }}>Today</span>
                              <h4 className="fw-bold mb-0 text-dark" style={{ fontSize: '1.2rem', letterSpacing: '-0.5px' }}>{todayVal}</h4>
                            </div>
                            <div style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0' }}></div>
                            <div>
                              <span className="text-muted d-block" style={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: 500 }}>Yesterday</span>
                              <h5 className="fw-semibold mb-0 text-muted" style={{ fontSize: '1.05rem', letterSpacing: '-0.5px' }}>{compareVal}</h5>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center mt-2" style={{ gap: '6px' }}>
                          <span
                            className="px-2 py-0.5 rounded-pill fw-bold d-inline-flex align-items-center text-nowrap"
                            style={{
                              fontSize: '0.65rem',
                              backgroundColor: isUp ? '#e6f4ea' : '#fce8e6',
                              color: isUp ? '#137333' : '#c5221f',
                              padding: '2px 6px'
                            }}
                          >
                            <i className={`fa-solid ${isUp ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'} me-1`}></i>
                            {Math.abs(percent).toFixed(1)}%
                          </span>
                          <span className="text-muted text-nowrap" style={{ fontSize: '0.55rem', fontWeight: 500 }}>
                            {diff >= 0 ? '+' : ''}{diff} rec
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Today vs Last Week */}
                  {(() => {
                    const todayVal = monitorData?.lastHourTracker?.todayLastHour ?? 0;
                    const compareVal = monitorData?.lastHourTracker?.lastWeekLastHour ?? 0;

                    const diff = todayVal - compareVal;
                    const percent = compareVal === 0 ? 0 : ((diff / compareVal) * 100);
                    const isUp = diff >= 0;

                    return (
                      <div className="col-6 ps-4 d-flex flex-column justify-content-between" style={{ height: '95px' }}>
                        <div>
                          <div className="d-flex align-items-center mb-1" style={{ gap: '8px' }}>
                            <span style={{ fontSize: '0.9rem', color: '#7886cb' }}>
                              <i className="fa-solid fa-history"></i>
                            </span>
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.75rem' }}>
                              Today vs Last Week
                            </span>
                          </div>

                          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <div>
                              <span className="text-muted d-block" style={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: 500 }}>Today</span>
                              <h4 className="fw-bold mb-0 text-dark" style={{ fontSize: '1.2rem', letterSpacing: '-0.5px' }}>{todayVal}</h4>
                            </div>
                            <div style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0' }}></div>
                            <div>
                              <span className="text-muted d-block" style={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: 500 }}>Last Week</span>
                              <h5 className="fw-semibold mb-0 text-muted" style={{ fontSize: '1.05rem', letterSpacing: '-0.5px' }}>{compareVal}</h5>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center mt-2" style={{ gap: '6px' }}>
                          <span
                            className="px-2 py-0.5 rounded-pill fw-bold d-inline-flex align-items-center text-nowrap"
                            style={{
                              fontSize: '0.65rem',
                              backgroundColor: isUp ? '#e6f4ea' : '#fce8e6',
                              color: isUp ? '#137333' : '#c5221f',
                              padding: '2px 6px'
                            }}
                          >
                            <i className={`fa-solid ${isUp ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'} me-1`}></i>
                            {Math.abs(percent).toFixed(1)}%
                          </span>
                          <span className="text-muted text-nowrap" style={{ fontSize: '0.55rem', fontWeight: 500 }}>
                            {diff >= 0 ? '+' : ''}{diff} rec
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Widget 2: Total Progress & Data Hari Ini */}
            {timeRange.startDate == null && timeRange.endDate == null && (
              <div className="row g-3">
                {/* Total Progress Card */}
                <div className="col-6">
                  <div
                    className="p-3 border-0 shadow-sm w-100 bg-white d-flex flex-column justify-content-between"
                    style={{
                      borderRadius: '16px',
                      borderLeft: '4px solid #2B75DA',
                      height: '110px'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ minWidth: 0 }}>
                        <span className="text-muted text-uppercase fw-semibold d-block text-truncate" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Total Progress
                        </span>
                        <h5 className="fw-bold mb-0 mt-1 text-dark text-truncate" style={{ letterSpacing: '-0.5px' }}>
                          {monitorData?.summaryData.completeTotal ?? 0}
                          <span className="text-muted fw-normal" style={{ fontSize: '0.75rem' }}> / {monitorData?.summaryData.jobTotal ?? 0}</span>
                        </h5>
                      </div>
                      <span className="text-primary opacity-75" style={{ fontSize: '1rem' }}>
                        <i className="fa-solid fa-list-check"></i>
                      </span>
                    </div>
                    {(() => {
                      const total = monitorData?.summaryData.jobTotal ?? 0;
                      const complete = monitorData?.summaryData.completeTotal ?? 0;
                      const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
                      return (
                        <div className="mt-1">
                          <div className="progress" style={{ height: '4px', background: '#e2e8f0' }} title={`${pct}% Completed`}>
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{ width: `${Math.min(pct, 100)}%` }}
                              aria-valuenow={pct}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            ></div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Data Hari Ini Card */}
                <div className="col-6">
                  <div
                    className="p-3 border-0 shadow-sm w-100 bg-white d-flex flex-column justify-content-between"
                    style={{
                      borderRadius: '16px',
                      borderLeft: '4px solid #0dcaf0',
                      height: '110px'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ minWidth: 0 }}>
                        <span className="text-muted text-uppercase fw-semibold d-block text-truncate" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Data Hari Ini
                        </span>
                        <h5 className="fw-bold mb-0 mt-1 text-dark" style={{ letterSpacing: '-0.5px' }}>
                          {monitorData?.summaryData.dataToday ?? 0}
                        </h5>
                      </div>
                      <span className="text-info opacity-75" style={{ fontSize: '1rem' }}>
                        <i className="fa-solid fa-calendar-day"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Widget 3: Status Breakdown */}
            <div className="p-3 border-0 shadow-sm w-100 bg-white flex-grow-1 d-flex flex-column" style={{ borderRadius: '16px' }}>
              <span className="text-muted text-uppercase fw-semibold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                Status Breakdown
              </span>

              {(() => {
                const chartResults = monitorData?.summaryData.results?.filter(dr => {
                  const labelLower = dr.label.toLowerCase();
                  return !labelLower.includes('total') && !labelLower.includes('jumlah') && !labelLower.includes('result');
                }) ?? [];

                if (chartResults.length === 0) return null;

                const totalItem = monitorData?.summaryData.results?.find(dr => {
                  const labelLower = dr.label.toLowerCase();
                  return labelLower.includes('total') || labelLower.includes('jumlah') || labelLower.includes('result');
                });
                const totalVal = totalItem ? totalItem.value : (chartResults.reduce((acc, curr) => acc + Number(curr.value ?? 0), 0).toString());

                const chartData = {
                  labels: chartResults.map(r => r.label),
                  datasets: [
                    {
                      data: chartResults.map(r => Number(r.value ?? 0)),
                      backgroundColor: chartResults.map(r => {
                        const labelLower = r.label.toLowerCase();
                        if (labelLower.includes('approve')) return '#198754';
                        if (labelLower.includes('qc') || labelLower.includes('done') || labelLower.includes('proses')) return '#ffc107';
                        if (labelLower.includes('reject')) return '#dc3545';
                        return '#6c757d';
                      }),
                      borderWidth: 0,
                      cutout: '72%',
                    },
                  ],
                };

                const chartOptions = {
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true }
                  },
                  maintainAspectRatio: false,
                  responsive: true
                };

                return (
                  <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 py-3 mt-2">
                    <div style={{ width: '150px', height: '150px', position: 'relative', flexShrink: 0, marginBottom: '24px' }}>
                      <Doughnut data={chartData} options={chartOptions} />
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center'
                        }}
                      >
                        <span className="text-muted" style={{ fontSize: '0.65rem', display: 'block', lineHeight: 1 }}>Total</span>
                        <strong className="fs-5 text-dark" style={{ lineHeight: 1.1 }}>{totalVal}</strong>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2 w-100 px-4" style={{ fontSize: '0.8rem', maxWidth: '240px' }}>
                      {[...(monitorData?.summaryData?.results || [])]
                        .sort((a, b) => a.sort - b.sort)
                        .map((dr) => {
                          const labelLower = dr.label.toLowerCase();
                          let dotColor = '#6c757d';
                          if (labelLower.includes('approve')) {
                            dotColor = '#198754';
                          } else if (labelLower.includes('qc') || labelLower.includes('done') || labelLower.includes('proses')) {
                            dotColor = '#ffc107';
                          } else if (labelLower.includes('reject')) {
                            dotColor = '#dc3545';
                          } else if (labelLower.includes('total') || labelLower.includes('jumlah') || labelLower.includes('result')) {
                            dotColor = '#2B75DA';
                          }
                          return (
                            <div className="d-flex align-items-center justify-content-between text-dark" key={dr.label}>
                              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                <span className="d-inline-block rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: dotColor, flexShrink: 0 }}></span>
                                <span className="text-muted fw-semibold text-truncate" style={{ fontSize: '0.75rem', maxWidth: '140px' }}>
                                  {dr.label}
                                </span>
                              </div>
                              <strong className="text-dark" style={{ fontSize: '0.85rem' }}>{dr.value}</strong>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right Column (Charts) */}
          <div className="col-12 col-lg-8 d-flex flex-column gap-3">
            {/* Widget 1: Daily Performance */}
            <Placeholder height={dailyPerformanceViewMode === 'map' ? 'auto' : 450}>
              <div style={{ width: "100%" }} className="d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h5 className="mb-0 fw-semibold text-dark">Daily Performance</h5>

                    {/* Date Navigation Pill */}
                    <div
                      className="bg-white rounded-pill px-2 py-1 d-inline-flex align-items-center border shadow-none position-relative"
                      style={{ borderColor: '#e2e8f0', gap: '4px', lineHeight: '1', width: 'fit-content' }}
                    >
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-secondary d-flex align-items-center justify-content-center"
                        style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }}
                        onClick={handlePrevDailyDate}
                        title="Previous day"
                      >
                        <i className="fa-solid fa-chevron-left" style={{ fontSize: '0.65rem', color: '#64748b' }}></i>
                      </button>

                      <div
                        className="d-flex align-items-center px-1"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                          try {
                            if (dailyDatePickerRef.current) {
                              if ('showPicker' in HTMLInputElement.prototype) {
                                dailyDatePickerRef.current.showPicker();
                              } else {
                                dailyDatePickerRef.current.focus();
                              }
                            }
                          } catch {
                            dailyDatePickerRef.current?.focus();
                          }
                        }}
                        title="Choose date"
                      >
                        <i className="fa-regular fa-calendar text-primary me-1" style={{ fontSize: '0.8rem' }}></i>
                        <span className="fw-semibold text-dark" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', lineHeight: '1' }}>
                          {formatDailyDateDisplay(activeDailyDate)}
                        </span>
                      </div>

                      <input
                        ref={dailyDatePickerRef}
                        type="date"
                        tabIndex={-1}
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '1px',
                          height: '1px',
                          pointerEvents: 'none',
                          bottom: 0,
                          left: '50%',
                        }}
                        value={formatDailyDateYMD(activeDailyDate)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            setDailyPerformanceDate(val);
                            fetchDailyPerformance(val);
                          } else {
                            handleResetDailyDate();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-secondary d-flex align-items-center justify-content-center"
                        style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }}
                        onClick={handleNextDailyDate}
                        title="Next day"
                      >
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.65rem', color: '#64748b' }}></i>
                      </button>
                    </div>

                    {/* Reset Button */}
                    {dailyPerformanceDate !== null && (
                      <button
                        type="button"
                        className="rounded-pill fw-semibold transition-all d-flex align-items-center border-0"
                        style={{
                          fontSize: '0.75rem',
                          backgroundColor: '#ffffff',
                          border: '1px solid #bfdbfe',
                          padding: '4px 12px',
                          color: '#2563eb',
                          lineHeight: '1.2',
                          cursor: 'pointer'
                        }}
                        onClick={handleResetDailyDate}
                      >
                        Reset to current date
                      </button>
                    )}

                    {isLoadingDailyPerformance && (
                      <div className="spinner-border spinner-border-sm text-primary ms-1" role="status" style={{ width: '1rem', height: '1rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-light p-1 rounded-pill d-inline-flex border" style={{ gap: '2px' }}>
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold transition-all ${dailyPerformanceViewMode === 'summary'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-secondary bg-transparent border-0'
                        }`}
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setDailyPerformanceViewMode('summary')}
                    >
                      <i className="fa-solid fa-chart-simple me-1"></i> Summary
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold transition-all ${dailyPerformanceViewMode === 'map'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-secondary bg-transparent border-0'
                        }`}
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setDailyPerformanceViewMode('map')}
                    >
                      <i className="fa-solid fa-map-location-dot me-1"></i> Map
                      {dailyResultMapData.length > 0 && (
                        <span className="badge rounded-pill bg-primary ms-1" style={{ fontSize: '0.65rem' }}>
                          {dailyResultMapData.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {dailyPerformanceViewMode === 'summary' ? (
                  <>
                    <PerformanceBar data={dailyPerformanceData} hideTitle={true} />
                    <div style={{ height: 240, marginTop: 10 }}>
                      <Chart
                        type="bar"
                        data={resultTrackerData}
                        options={{
                          ...options,
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex-grow-1" style={{ minHeight: 0 }}>
                    <DailyPerformanceMapView
                      performanceData={dailyPerformanceData}
                      resultMapData={dailyResultMapData}
                      onViewOfficerDetail={handleViewOfficerDetail}
                      onOpenResultDetail={handleOpenResultDetail}
                    />
                  </div>
                )}
              </div>
            </Placeholder>

            {/* Widget 2: Cumulative Acquisition */}
            <Placeholder height={320}>
              <div style={{ width: "100%", height: "100%" }}>
                <Chart
                  type="bar"
                  data={trackerData}
                  options={{
                    ...options,
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </div>
            </Placeholder>
          </div>

          {/* Toggle Legacy View */}
          <div className="col-12 mt-4 mb-2 d-flex justify-content-end">
            <div className="d-flex align-items-center gap-3 cursor-pointer" onClick={() => setIsLegacyView(!isLegacyView)}>
              <span className="fw-semibold text-secondary" style={{ cursor: 'pointer', userSelect: 'none' }}>
                Legacy View
              </span>
              <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                <input
                  className="form-check-input m-0 cursor-pointer border shadow-sm"
                  type="checkbox"
                  role="switch"
                  style={{ width: '40px', height: '22px' }}
                  checked={isLegacyView}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <Placeholder height={750}>
              <div className="d-flex flex-column h-100">
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-semibold">By Area</h5>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={handleCopyAreaToClipboard} title="Copy data to Excel">
                      <i className="fa-regular fa-copy"></i> Copy
                    </button>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search Area..."
                      style={{ width: 160 }}
                      value={areaSearch}
                      onChange={(e) => setAreaSearch(e.target.value)}
                    />
                  </div>
                </div>
                {isLegacyView ? (
                  <div className="mb-2 overflow-auto flex-grow-1">
                    <table className="table mb-0 table-condensed table-hover text-nowrap">
                      <thead
                        className="table-primary text-nowrap"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          background: "white",
                        }}
                      >
                        <tr>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleAreaSort("name")}
                          >
                            Name {renderSortIndicator("name", "area")}
                          </th>
                          {selectedDateRangeKey === "all" && hasAreaResultColumns && (
                            <th
                              style={{ cursor: "pointer" }}
                              onClick={() => handleAreaSort("target")}
                            >
                              Target {renderSortIndicator("target", "area")}
                            </th>
                          )}
                          {[...(sortedAreas?.[0]?.results || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                            <th
                              key={`area-header-${rd.label}`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleAreaSort(rd.label)}
                            >
                              {rd.label} {renderSortIndicator(rd.label, "area")}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {monitorData?.jobList && (
                        <tbody>
                          {paginatedAreas?.map((job) => {
                            const desc = job.description || job.groupDescription;
                            return (
                              <tr key={job.id}>
                                <td
                                  className="text-primary"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleOpenAreaDetail(job.id, job.name, desc || "")}
                                >
                                  <div className="fw-semibold text-primary">{job.name}</div>
                                  {desc && (
                                    <AreaDescriptionWithTooltip text={desc} fontSize="0.72rem" maxWidth="260px" />
                                  )}
                                  {areaBadgePopover && areaBadgePopover.jobId === job.id && (
                                    <div
                                      style={{
                                        position: "fixed",
                                        left: areaBadgePopover.x + 10,
                                        top: areaBadgePopover.y - 10,
                                        transform: "translateY(-100%)",
                                        background: "white",
                                        border: "1px solid #ddd",
                                        borderRadius: 10,
                                        padding: 14,
                                        zIndex: 2000,
                                        width: 240,
                                        pointerEvents: "none"
                                      }}
                                    >
                                      <div style={{ fontSize: 13, color: "#333", display: "flex", flexDirection: "column", gap: 6 }}>
                                        <div>
                                          <span style={{ fontWeight: 600 }}>Outstanding Target:</span>{" "}
                                          <span className="text-secondary fw-bold">{areaBadgePopover.outstandingTarget}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                {selectedDateRangeKey === "all" && hasAreaResultColumns && (
                                  <td>{job.target ?? 0}</td>
                                )}
                                {[...(job.results || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                                  <td key={`area-${job.id}-${rd.label}`}>{rd.value}</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      )}
                    </table>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2 px-1 flex-wrap gap-2">
                      {!isLegacyView ? (
                        <div className="d-flex align-items-center">
                          <span className="text-muted me-2 fw-semibold" style={{ fontSize: '0.75rem' }}><i className="fa-solid fa-sort me-1"></i>Sort:</span>
                          <CustomDropdown
                            value={areaSortConfig?.key || "Result"}
                            options={[
                              { id: "name", name: `Name ${areaSortConfig?.key === "name" ? (areaSortConfig.direction === 'asc' ? '↑' : '↓') : ''}` },
                              ...(selectedDateRangeKey === "all" && hasAreaResultColumns ? [{ id: "target", name: `Target ${areaSortConfig?.key === "target" ? (areaSortConfig.direction === 'asc' ? '↑' : '↓') : ''}` }] : []),
                              ...[...(sortedAreas?.[0]?.results || [])].map(rd => ({
                                id: rd.label,
                                name: `${rd.label} ${areaSortConfig?.key === rd.label ? (areaSortConfig.direction === 'asc' ? '↑' : '↓') : ''}`
                              }))
                            ]}
                            onChange={(opt) => handleAreaSort(opt.id as string)}
                            align="left"
                          />
                        </div>
                      ) : <div />}
                      {sortedAreas?.[0]?.results && (
                        <div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '0.75rem' }}>
                          {[...(sortedAreas[0].results || [])]
                            .filter(r => r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target')
                            .sort((a, b) => a.sort - b.sort)
                            .map((item) => (
                              <div key={`legend-area-${item.label}`} className="d-flex align-items-center gap-1 text-muted">
                                <span className="rounded-circle" style={{ width: 8, height: 8, backgroundColor: item.color || '#6c757d', display: 'inline-block' }}></span>
                                <span className="fw-semibold">{item.label}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="mb-2 overflow-auto flex-grow-1 px-1">
                      <div className="d-flex flex-column gap-1 pb-2">
                        {paginatedAreas?.map((job, idx) => {
                          const targetValue = (selectedDateRangeKey === "all" && hasAreaResultColumns) ? (job.target ?? 0) : (Number(job.results?.find(r => r.label.toLowerCase() === 'target')?.value) || 0);
                          const approvedItem = job.results?.find(r => r.label && r.label.toLowerCase() === 'approved');
                          const doneItem = job.results?.find(r => r.label && r.label.toLowerCase() === 'done');

                          const approvedVal = approvedItem?.value ?? 0;
                          const doneVal = doneItem?.value ?? 0;
                          const targetPct = targetValue > 0 ? Math.round(((Number(approvedVal) + Number(doneVal)) / targetValue) * 100) : 0;

                          const primaryBlueApprovedItem = approvedItem ? { ...approvedItem, color: '#0d6efd' } : null;
                          const targetProgressItems = [primaryBlueApprovedItem, doneItem].filter((item): item is NonNullable<typeof item> => Boolean(item));
                          const breakdownItems = job.results?.filter(r => r.label && r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target') || [];
                          const desc = job.description || job.groupDescription;

                          return (
                            <div
                              key={`card-${job.id}`}
                              className="card border-0 border-bottom shadow-none rounded-0"
                              style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out', backgroundColor: 'transparent' }}
                              onClick={() => handleOpenAreaDetail(job.id, job.name, desc || "")}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div className="card-body py-1.5 px-2 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2" style={{ padding: '5px 8px' }}>
                                <div className="d-flex align-items-center pe-3 me-1 border-end" style={{ flex: '1 1 0', minWidth: 0, borderColor: '#e2e8f0' }}>
                                  <span className="text-muted fw-semibold me-2 text-end" style={{ fontSize: '0.75rem', width: '22px', flexShrink: 0 }}>
                                    {(areaCurrentPage - 1) * areaPageSize + idx + 1}.
                                  </span>
                                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <div
                                      className="fw-bold text-dark text-truncate"
                                      style={{ fontSize: '0.85rem' }}
                                      title={job.name}
                                    >
                                      {job.name}
                                    </div>
                                    {desc && (
                                      <AreaDescriptionWithTooltip text={desc} fontSize="0.7rem" />
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex flex-column" style={{ flex: '1 1 0', minWidth: 0 }}>
                                  <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                                    <div className="fw-semibold me-auto text-nowrap" style={{ fontSize: '0.75rem' }}>
                                      <span className="fw-bold" style={{ color: '#0d6efd' }}>{approvedVal}</span>
                                      {Number(doneVal) > 0 && (
                                        <span className="fw-bold ms-1" style={{ color: doneItem?.color || '#fd7e14' }}>({doneVal})</span>
                                      )}
                                      <span className="text-muted fw-semibold ms-1">/ {targetValue}</span>
                                      {targetValue > 0 && (
                                        <span className="badge bg-light text-secondary ms-1 border" style={{ fontSize: '0.65rem' }}>{targetPct}%</span>
                                      )}
                                    </div>
                                    <div className="d-flex gap-2 align-items-center justify-content-end flex-nowrap">
                                      {[...breakdownItems].sort((a, b) => a.sort - b.sort).map((rd) => (
                                        <div key={`area-card-${job.id}-${rd.label}`} className="fw-bold lh-1" style={{ color: rd.color || '#333', fontSize: '0.8rem' }} title={rd.label}>
                                          {rd.value}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center gap-2 w-100 mt-1">
                                    {targetValue > 0 && (
                                      <div className="flex-fill" style={{ flex: '1 1 0', minWidth: 0 }} title={`Target Progress: ${approvedVal} approved, ${doneVal} done / ${targetValue} target`}>
                                        <StackedProgressBar
                                          results={targetProgressItems}
                                          target={targetValue}
                                          height={5}
                                        />
                                      </div>
                                    )}

                                    {breakdownItems.length > 0 && (
                                      <div className="flex-fill" style={{ flex: '1 1 0', minWidth: 0 }} title="Status Breakdown">
                                        <StackedProgressBar
                                          results={breakdownItems}
                                          target={0}
                                          height={5}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>)}
                <div className="mt-3 d-flex justify-content-end align-items-center">
                  <div className="me-4 d-flex align-items-center">
                    <label className="me-2">Rows per page:</label>
                    <CustomDropdown
                      value={areaPageSize}
                      options={PageSizeOption.options}
                      onChange={(opt) => {
                        setAreaPageSize(Number(opt.id));
                        setAreaCurrentPage(1);
                      }}
                      align="top"
                    />
                  </div>

                  <div className="d-flex align-items-center">
                    <span className="me-3">
                      {`${(areaCurrentPage - 1) * areaPageSize + 1}–${Math.min(
                        areaCurrentPage * areaPageSize,
                        sortedAreas?.length ?? 0
                      )} of ${sortedAreas?.length ?? 0}`}
                    </span>

                    <nav>
                      <ul className="mb-0 pagination">
                        <li className={`page-item ${areaCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleAreaPageChange(1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${areaCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleAreaPageChange(areaCurrentPage - 1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-left"></i>
                          </button>
                        </li>
                        <li className="page-item disabled">
                          <span className="page-link" style={{ border: 0 }}>{areaCurrentPage}</span>
                        </li>
                        <li className={`page-item ${areaCurrentPage === totalAreaPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleAreaPageChange(areaCurrentPage + 1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-right"></i>
                          </button>
                        </li>
                        <li className={`page-item ${areaCurrentPage === totalAreaPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleAreaPageChange(totalAreaPages)} style={{ border: 0 }}>
                            <i className="fa fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </Placeholder>
          </div>
          <div className="col-12 col-md-6">
            <Placeholder height={750}>
              <div className="d-flex flex-column h-100">
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 fw-semibold me-4">By Officer</h5>
                    {
                      monitorData?.tribes && monitorData?.tribes?.length > 0 && (
                        <ul className="nav nav-pills h-100">
                          <li className="m-0 nav-item h-100">
                            <button
                              className={`h-100 nav-link ${selectedMenuOfficer == "user" ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedMenuOfficer("user")
                                setOfficerCurrentPage(1)
                                setOfficerPageSize(10)
                              }}
                            >
                              Users
                            </button>
                          </li>
                          <li className="m-0 nav-item h-100">
                            <button
                              className={`h-100 nav-link ${selectedMenuOfficer == "tribe" ? 'active' : ''}`}
                              onClick={() => {
                                setSelectedMenuOfficer("tribe")
                                setOfficerCurrentPage(1)
                                setOfficerPageSize(10)
                              }}
                            >
                              Tribes
                            </button>
                          </li>
                        </ul>
                      )
                    }
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={handleCopyOfficerToClipboard} title="Copy data to Excel">
                      <i className="fa-regular fa-copy"></i> Copy
                    </button>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search..."
                      style={{ width: 160 }}
                      value={officerSearch}
                      onChange={(e) => setOfficerSearch(e.target.value)}
                    />
                  </div>
                </div>
                {isLegacyView ? (
                  <div className="mb-2 overflow-auto flex-grow-1">
                    <table className="table mb-0 table-condensed table-hover text-nowrap">
                      <thead
                        className="table-primary text-nowrap"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          background: "white",
                        }}
                      >
                        <tr>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleOfficerSort("name")}
                          >
                            Name {renderSortIndicator("name", "officer")}
                          </th>
                          {[...(sortedOfficers?.[0]?.results || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                            <th
                              key={`officer-header-${rd.label}`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleOfficerSort(rd.label)}
                            >
                              {rd.label} {renderSortIndicator(rd.label, "officer")}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMenuOfficer == "user" && paginatedOfficers?.map((contributor) => (
                          <tr key={contributor.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                {contributor.profilePictureUrl ? (
                                  <img
                                    src={contributor.profilePictureUrl}
                                    alt=""
                                    className="rounded-circle me-2"
                                    style={{ width: 24, height: 24, objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center me-2 bg-primary text-white fw-bold"
                                    style={{ width: 24, height: 24, fontSize: '10px', flexShrink: 0 }}
                                  >
                                    {getInitials(contributor.name)}
                                  </div>
                                )}
                                <div className="d-flex align-items-center">
                                  <span
                                    className="cursor-pointer text-decoration-none text-primary"
                                    onClick={() => handleViewOfficerDetail(contributor.id)}
                                    style={{
                                      fontWeight: 'inherit',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                    title="View user details"
                                  >
                                    {contributor.name}
                                  </span>
                                  {contributor.grade && (
                                    <span
                                      className="badge ms-1.5 px-1.5 py-0.5 rounded-pill text-white fw-bold shadow-xs flex-shrink-0"
                                      style={{
                                        fontSize: '0.65rem',
                                        backgroundColor: (() => {
                                          const clean = contributor.grade.toUpperCase().replace('GRADE', '').trim();
                                          if (clean.startsWith('A')) return '#10B981';
                                          if (clean.startsWith('B')) return '#EAB308';
                                          if (clean.startsWith('C')) return '#F97316';
                                          return '#EF4444';
                                        })()
                                      }}
                                      title={contributor.gradeScore != null ? `Score: ${contributor.gradeScore.toFixed(1)} / 100` : contributor.grade}
                                    >
                                      {contributor.grade}
                                    </span>
                                  )}
                                  {contributor.outstandingCount != null && contributor.outstandingCount > 0 && (
                                    <span
                                      className="badge bg-secondary rounded-pill ms-1"
                                      style={{ cursor: 'pointer' }}
                                      onMouseEnter={(e) => handleUserBadgeMouseEnter(e, contributor)}
                                      onMouseLeave={handleUserBadgeMouseLeave}
                                    >
                                      {contributor.outstandingCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {userBadgePopover && (
                                <div
                                  style={{
                                    position: "fixed",
                                    left: userBadgePopover.x - 100,
                                    top: userBadgePopover.y - 10,
                                    transform: "translate(-50%, -100%)",
                                    background: "white",
                                    border: "1px solid #ddd",
                                    borderRadius: 10,
                                    padding: 14,
                                    zIndex: 2000,
                                    width: 240,
                                    pointerEvents: "none"
                                  }}
                                >
                                  <div style={{ fontSize: 13, color: "#333", display: "flex", flexDirection: "column", gap: 6 }}>
                                    <div>
                                      <span style={{ fontWeight: 600 }}>Outstanding Task:</span>{" "}
                                      <span className="text-secondary fw-bold">{userBadgePopover.contributor.outstandingCount}</span>
                                    </div>
                                    <div>
                                      <span style={{ fontWeight: 600 }}>Last Active:</span>{" "}
                                      {userBadgePopover.contributor.lastAccessAt || <span className="text-muted fst-italic">Never</span>}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                            {[...(contributor.results || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                              <td key={`officer-${contributor.id}-${rd.label}`}>{rd.value}</td>
                            ))}
                          </tr>
                        ))}
                        {selectedMenuOfficer == "tribe" && paginatedOfficers?.map((tribe) => (
                          <tr key={tribe.id}>
                            <td>
                              <span className="cursor-pointer text-decoration-none">
                                {tribe.name}
                              </span>
                            </td>
                            {[...(tribe.results || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                              <td key={`officer-${tribe.id}-${rd.label}`}>{rd.value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2 px-1 flex-wrap gap-2">
                      {!isLegacyView ? (
                        <div className="d-flex align-items-center">
                          <span className="text-muted me-2 fw-semibold" style={{ fontSize: '0.75rem' }}><i className="fa-solid fa-sort me-1"></i>Sort:</span>
                          <CustomDropdown
                            value={officerSortConfig?.key || "Result"}
                            options={[
                              { id: "name", name: `Name ${officerSortConfig?.key === "name" ? (officerSortConfig.direction === 'asc' ? '↑' : '↓') : ''}` },
                              ...[...(sortedOfficers?.[0]?.results || [])].map(rd => ({
                                id: rd.label,
                                name: `${rd.label} ${officerSortConfig?.key === rd.label ? (officerSortConfig.direction === 'asc' ? '↑' : '↓') : ''}`
                              }))
                            ]}
                            onChange={(opt) => handleOfficerSort(opt.id as string)}
                            align="left"
                          />
                        </div>
                      ) : <div />}
                      {sortedOfficers?.[0]?.results && (
                        <div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: '0.75rem' }}>
                          {[...(sortedOfficers[0].results || [])]
                            .filter(r => r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target' && r.label.toLowerCase() !== 'tribe name' && r.label.toLowerCase() !== 'tribe')
                            .sort((a, b) => a.sort - b.sort)
                            .map((item) => (
                              <div key={`legend-officer-${item.label}`} className="d-flex align-items-center gap-1 text-muted">
                                <span className="rounded-circle" style={{ width: 8, height: 8, backgroundColor: item.color || '#6c757d', display: 'inline-block' }}></span>
                                <span className="fw-semibold">{item.label}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="mb-2 overflow-auto flex-grow-1 px-1">
                      <div className="d-flex flex-column gap-1 pb-2">
                        {selectedMenuOfficer === "user" && paginatedOfficers?.map((contributor, idx) => {
                          const tribeResult = contributor.results?.find(r => r.label.toLowerCase() === 'tribe name' || r.label.toLowerCase() === 'tribe');
                          const tribeNameValue = tribeResult?.value || contributor.tribeName;
                          const filteredResults = contributor.results?.filter(r => r.label.toLowerCase() !== 'tribe name' && r.label.toLowerCase() !== 'tribe' && r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target') || [];

                          return (
                            <div
                              key={`card-officer-${contributor.id}`}
                              className="card border-0 border-bottom shadow-none rounded-0"
                              style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out', backgroundColor: 'transparent' }}
                              onClick={() => handleViewOfficerDetail(contributor.id)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div className="card-body py-1.5 px-2 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2" style={{ padding: '5px 8px' }}>
                                <div className="d-flex align-items-center pe-3 me-1 border-end" style={{ flex: '1 1 0', minWidth: 0, borderColor: '#e2e8f0' }}>
                                  <span className="text-muted fw-semibold me-2 text-end" style={{ fontSize: '0.75rem', width: '22px', flexShrink: 0 }}>
                                    {(officerCurrentPage - 1) * officerPageSize + idx + 1}.
                                  </span>
                                  {contributor.profilePictureUrl ? (
                                    <img
                                      src={contributor.profilePictureUrl}
                                      alt=""
                                      className="rounded-circle me-2"
                                      style={{ width: 30, height: 30, objectFit: 'cover', flexShrink: 0 }}
                                    />
                                  ) : (
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center me-2 bg-light text-primary fw-bold"
                                      style={{ width: 30, height: 30, fontSize: '11px', flexShrink: 0 }}
                                    >
                                      {getInitials(contributor.name)}
                                    </div>
                                  )}
                                  <div className="text-truncate">
                                    <div className="d-flex align-items-center gap-1">
                                      <span
                                        className="fw-bold text-dark cursor-pointer text-decoration-none text-truncate"
                                        title={contributor.name}
                                        style={{ fontSize: '0.85rem' }}
                                      >
                                        {contributor.name}
                                      </span>
                                      {contributor.grade && (
                                        <span
                                          className="badge px-1.5 py-0.5 rounded-pill text-white fw-bold shadow-xs flex-shrink-0"
                                          style={{
                                            fontSize: '0.65rem',
                                            backgroundColor: (() => {
                                              const clean = contributor.grade.toUpperCase().replace('GRADE', '').trim();
                                              if (clean.startsWith('A')) return '#10B981';
                                              if (clean.startsWith('B')) return '#EAB308';
                                              if (clean.startsWith('C')) return '#F97316';
                                              return '#EF4444';
                                            })()
                                          }}
                                          title={contributor.gradeScore != null ? `Score: ${contributor.gradeScore.toFixed(1)} / 100` : contributor.grade}
                                        >
                                          {contributor.grade}
                                        </span>
                                      )}
                                    </div>
                                    <div className="d-flex align-items-center">
                                      {tribeNameValue ? (
                                        <span className="text-muted text-truncate" style={{ fontSize: '0.75rem', maxWidth: '180px' }} title={String(tribeNameValue)}>
                                          {tribeNameValue}
                                        </span>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>No Tribe</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex flex-column" style={{ flex: '1 1 0', minWidth: 0 }}>
                                  <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                                    <div className="text-muted fw-semibold me-auto text-nowrap" style={{ fontSize: '0.7rem' }}>
                                      Total: <span className="text-dark fw-bold ms-1">
                                        {contributor.results?.find(r => r.label.toLowerCase() === 'result')?.value ?? 0}
                                      </span>
                                    </div>
                                    <div className="d-flex gap-2 align-items-center justify-content-end flex-nowrap">
                                      {[...filteredResults].sort((a, b) => a.sort - b.sort).map((rd) => (
                                        <div key={`officer-card-${contributor.id}-${rd.label}`} className="fw-bold lh-1" style={{ color: rd.color || '#333', fontSize: '0.8rem' }} title={rd.label}>
                                          {rd.value}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {filteredResults.length > 0 && (
                                    <StackedProgressBar
                                      results={filteredResults}
                                      target={Number(contributor.results?.find(r => r.label.toLowerCase() === 'result')?.value) || 0}
                                    />
                                  )}
                                  <div className="d-flex align-items-center justify-content-between mt-1 gap-2 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <div>
                                      {contributor.outstandingCount != null && contributor.outstandingCount > 0 && (
                                        <span
                                          className="text-primary fw-semibold cursor-pointer"
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseEnter={(e) => handleUserBadgeMouseEnter(e, contributor)}
                                          onMouseLeave={handleUserBadgeMouseLeave}
                                        >
                                          Outstanding: {contributor.outstandingCount} task
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-muted text-end ms-auto" title={contributor.lastAccessAt ? new Date(contributor.lastAccessAt).toLocaleString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}>
                                      Last Active: {getRelativeTime(contributor.lastAccessAt)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {selectedMenuOfficer === "tribe" && paginatedOfficers?.map((tribe, idx) => (
                          <div
                            key={`card-tribe-${tribe.id}`}
                            className="card border-0 border-bottom shadow-none rounded-0"
                            style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out', backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div className="card-body py-1.5 px-2 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2" style={{ padding: '5px 8px' }}>
                              <div className="d-flex align-items-center pe-3 me-1 border-end" style={{ flex: '1 1 0', minWidth: 0, borderColor: '#e2e8f0' }}>
                                <span className="text-muted fw-semibold me-2 text-end" style={{ fontSize: '0.75rem', width: '22px', flexShrink: 0 }}>
                                  {(officerCurrentPage - 1) * officerPageSize + idx + 1}.
                                </span>
                                <div
                                  className="fw-bold text-dark text-truncate"
                                  style={{ flex: '1 1 0', minWidth: 0, fontSize: '0.85rem' }}
                                  title={tribe.name}
                                >
                                  {tribe.name}
                                </div>
                              </div>
                              <div className="d-flex flex-column" style={{ flex: '1 1 0', minWidth: 0 }}>
                                <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                                  <div className="text-muted fw-semibold me-auto text-nowrap" style={{ fontSize: '0.7rem' }}>
                                    Total: <span className="text-dark fw-bold ms-1">
                                      {tribe.results?.find(r => r.label.toLowerCase() === 'result')?.value ?? 0}
                                    </span>
                                  </div>
                                  <div className="d-flex gap-2 align-items-center justify-content-end flex-nowrap">
                                    {[...(tribe.results?.filter(r => r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target') || [])].sort((a, b) => a.sort - b.sort).map((rd) => (
                                      <div key={`tribe-card-${tribe.id}-${rd.label}`} className="fw-bold lh-1" style={{ color: rd.color || '#333', fontSize: '0.8rem' }} title={rd.label}>
                                        {rd.value}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {tribe.results && tribe.results.length > 0 && (
                                  <StackedProgressBar
                                    results={tribe.results?.filter(r => r.label.toLowerCase() !== 'result' && r.label.toLowerCase() !== 'target') || []}
                                    target={Number(tribe.results?.find(r => r.label.toLowerCase() === 'result')?.value) || 0}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>)}
                <div className="mt-3 d-flex justify-content-end align-items-center">
                  <div className="me-4 d-flex align-items-center">
                    <label className="me-2">Rows per page:</label>
                    <CustomDropdown
                      value={officerPageSize}
                      options={PageSizeOption.options}
                      onChange={(opt) => {
                        setOfficerPageSize(Number(opt.id));
                        setOfficerCurrentPage(1);
                      }}
                      align="top"
                    />
                  </div>

                  <div className="d-flex align-items-center">
                    <span className="me-3">
                      {`${(officerCurrentPage - 1) * officerPageSize + 1}–${Math.min(
                        officerCurrentPage * officerPageSize,
                        sortedOfficers?.length ?? 0
                      )} of ${sortedOfficers?.length ?? 0}`}
                    </span>

                    <nav>
                      <ul className="mb-0 pagination">
                        <li className={`page-item ${officerCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleOfficerPageChange(1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${officerCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleOfficerPageChange(officerCurrentPage - 1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-left"></i>
                          </button>
                        </li>
                        <li className="page-item disabled">
                          <span className="page-link" style={{ border: 0 }}>{officerCurrentPage}</span>
                        </li>
                        <li className={`page-item ${officerCurrentPage === totalOfficerPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleOfficerPageChange(officerCurrentPage + 1)} style={{ border: 0 }}>
                            <i className="fa fa-angle-right"></i>
                          </button>
                        </li>
                        <li className={`page-item ${officerCurrentPage === totalOfficerPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handleOfficerPageChange(totalOfficerPages)} style={{ border: 0 }}>
                            <i className="fa fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </Placeholder>
          </div>

          {/* Officer Daily Matrix */}
          <div className="col-12 mt-4 animate__animated animate__fadeIn">
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom pb-3 mb-3">
                <div>
                  <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>Officer Daily Matrix</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                    {matrixMetricMode === 'percentage'
                      ? `Day to day acquisition volume percentage per ${matrixGroupMode}.`
                      : `Day to day acquisition volume count per ${matrixGroupMode}.`}
                  </p>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div ref={matrixGroupNavRef} className="nav-pills-animated">
                    <div
                      className="nav-pill-indicator"
                      style={matrixGroupIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link btn ${matrixGroupMode === 'officer' ? 'active' : ''}`}
                      onClick={() => {
                        setMatrixGroupMode('officer');
                        setMatrixSortConfig(null);
                      }}
                    >
                      Officer
                    </button>
                    <button
                      type="button"
                      className={`nav-link btn ${matrixGroupMode === 'tribe' ? 'active' : ''}`}
                      onClick={() => {
                        setMatrixGroupMode('tribe');
                        setMatrixSortConfig(null);
                      }}
                    >
                      Tribe
                    </button>
                    <button
                      type="button"
                      className={`nav-link btn ${matrixGroupMode === 'group' ? 'active' : ''}`}
                      onClick={() => {
                        setMatrixGroupMode('group');
                        setMatrixSortConfig(null);
                      }}
                    >
                      Group
                    </button>
                  </div>

                  <div style={{ width: 220 }}>
                    <MultiSelectDropdown
                      key={matrixGroupMode}
                      options={dropdownOptions}
                      placeholder={
                        matrixGroupMode === 'officer'
                          ? (selectedMatrixOfficers.length === 0 ? "All Officer" : "Select Officers...")
                          : matrixGroupMode === 'tribe'
                            ? (selectedMatrixTribes.length === 0 ? "All Tribe" : "Select Tribes...")
                            : (selectedMatrixGroups.length === 0 ? "All Group" : "Select Groups...")
                      }
                      allSelectedText={
                        matrixGroupMode === 'officer'
                          ? "All Officer"
                          : matrixGroupMode === 'tribe'
                            ? "All Tribe"
                            : "All Group"
                      }
                      showSelectAll={true}
                      value={
                        matrixGroupMode === 'officer'
                          ? selectedMatrixOfficers
                          : matrixGroupMode === 'tribe'
                            ? selectedMatrixTribes
                            : selectedMatrixGroups
                      }
                      onChange={(selected) => {
                        if (matrixGroupMode === 'officer') {
                          setSelectedMatrixOfficers(selected);
                        } else if (matrixGroupMode === 'tribe') {
                          setSelectedMatrixTribes(selected);
                        } else {
                          setSelectedMatrixGroups(selected);
                        }
                      }}
                      stayOpenOnUpdate={true}
                      subKey={matrixGroupMode === 'officer' ? 'subLabel' : undefined}
                      searchKeys={matrixGroupMode === 'officer' ? ['name', 'tribeName', 'subLabel'] : undefined}
                    />
                  </div>

                  <div ref={matrixMetricNavRef} className="nav-pills-animated">
                    <div
                      className="nav-pill-indicator"
                      style={matrixMetricIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link btn ${matrixMetricMode === 'count' ? 'active' : ''}`}
                      onClick={() => setMatrixMetricMode('count')}
                    >
                      Count
                    </button>
                    <button
                      type="button"
                      className={`nav-link btn ${matrixMetricMode === 'percentage' ? 'active' : ''}`}
                      onClick={() => setMatrixMetricMode('percentage')}
                    >
                      Percentage
                    </button>
                  </div>

                  <div ref={matrixValueNavRef} className="nav-pills-animated">
                    <div
                      className="nav-pill-indicator"
                      style={matrixValueIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link btn ${matrixValueMode === 'daily' ? 'active' : ''}`}
                      onClick={() => setMatrixValueMode('daily')}
                    >
                      Daily
                    </button>
                    <button
                      type="button"
                      className={`nav-link btn ${matrixValueMode === 'cumulative' ? 'active' : ''}`}
                      onClick={() => setMatrixValueMode('cumulative')}
                    >
                      Cumulative
                    </button>
                  </div>

                  <div ref={matrixViewModeNavRef} className="nav-pills-animated">
                    <div
                      className="nav-pill-indicator"
                      style={matrixViewModeIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`nav-link btn ${matrixViewMode === 'table' ? 'active' : ''}`}
                      onClick={() => setMatrixViewMode('table')}
                      style={{ fontSize: '0.75rem', padding: '6px 12px', borderRadius: '8px' }}
                    >
                      <i className="fa-solid fa-table me-1"></i>Table
                    </button>
                    <button
                      type="button"
                      className={`nav-link btn ${matrixViewMode === 'chart' ? 'active' : ''}`}
                      onClick={() => setMatrixViewMode('chart')}
                      style={{ fontSize: '0.75rem', padding: '6px 12px', borderRadius: '8px' }}
                    >
                      <i className="fa-solid fa-chart-line me-1"></i>Chart
                    </button>
                  </div>
                </div>
              </div>

              {groupedSourceRows && groupedSourceRows.length > 0 ? (
                matrixViewMode === 'table' ? (
                  <div className="table-responsive" style={{ overflowX: 'auto', position: 'relative' }}>
                    <table className="table table-bordered table-hover align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '1800px', borderColor: '#e2e8f0' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', color: '#475569', fontSize: '0.8rem' }}>
                          <th
                            className="fw-bold text-dark cursor-pointer"
                            onClick={() => handleMatrixSort('name')}
                            style={{ position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 3, width: '220px', minWidth: '220px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}
                          >
                            {matrixGroupMode === 'officer' ? 'Officer Name' : matrixGroupMode === 'tribe' ? 'Tribe Name' : 'Group'} {renderSortIndicator('name', 'matrix')}
                          </th>
                          <th
                            className="text-center fw-bold bg-light text-dark cursor-pointer"
                            onClick={() => handleMatrixSort('total')}
                            style={{ position: 'sticky', left: '220px', backgroundColor: '#f1f5f9', zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)', width: '100px', minWidth: '100px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}
                          >
                            {matrixMetricMode === 'percentage' ? 'Total (%)' : 'Total Data'} {renderSortIndicator('total', 'matrix')}
                          </th>
                          {matrixDates.map((date: string) => (
                            <th
                              key={date}
                              className="text-center fw-bold cursor-pointer"
                              onClick={() => handleMatrixSort(date)}
                              style={{ minWidth: '85px', whiteSpace: 'nowrap', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}
                            >
                              {date} {renderSortIndicator(date, 'matrix')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody style={{ fontSize: '0.85rem' }}>
                        {sortedMatrixRows.length === 0 ? (
                          <tr>
                            <td colSpan={2 + matrixDates.length} className="text-center py-4 text-muted">
                              No data selected. Please select filters from the dropdown.
                            </td>
                          </tr>
                        ) : (
                          sortedMatrixRows.map((row: any) => {
                            const rowTotalCount = row.days.reduce((sum: number, d: any) => sum + (d.count || 0), 0);
                            let rowTotal = matrixMetricMode === 'percentage'
                              ? (row.target && row.target > 0
                                ? (rowTotalCount / row.target)
                                : row.days.reduce((sum: number, d: any) => sum + (d.percentage || 0), 0))
                              : rowTotalCount;
                            if (matrixMetricMode === 'percentage' && Math.abs(rowTotal - 1.0) <= 0.0002) {
                              rowTotal = 1.0;
                            }
                            const formattedRowTotal = matrixMetricMode === 'percentage'
                              ? (rowTotal === 0 ? '0%' : `${(rowTotal * 100).toFixed(2)}%`)
                              : rowTotal;
                            return (
                              <tr key={matrixGroupMode === 'officer' ? row.userId : matrixGroupMode === 'tribe' ? row.tribeName : row.groupName}>
                                <td className="fw-semibold text-dark" style={{ position: 'sticky', left: 0, backgroundColor: '#ffffff', zIndex: 2, borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    {matrixGroupMode === 'officer' ? (
                                      <>
                                        {row.profilePicture ? (
                                          <img
                                            src={row.profilePicture}
                                            alt={row.userName}
                                            className="rounded-circle flex-shrink-0"
                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                          />
                                        ) : (
                                          <div
                                            className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                            style={{ width: '32px', height: '32px', background: '#5f7185', fontSize: '11px', letterSpacing: '0.5px' }}
                                          >
                                            {row.userName.trim().split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                          </div>
                                        )}
                                        <div className="d-flex flex-column align-items-start">
                                          <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{row.userName}</span>
                                          {(() => {
                                            const showTribe = Boolean(row.tribeName && row.tribeName.trim().toLowerCase() !== 'no tribe');
                                            if (!showTribe) return null;
                                            return (
                                              <span className="text-muted" style={{ fontSize: '0.75rem', marginTop: '2px', fontWeight: 'normal' }}>
                                                {[showTribe && `Tribe: ${row.tribeName}`].filter(Boolean).join(' • ')}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </>
                                    ) : matrixGroupMode === 'tribe' ? (
                                      <div className="d-flex align-items-center gap-2">
                                        <div
                                          className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                          style={{ width: '32px', height: '32px', background: '#8b5cf6', fontSize: '11px' }}
                                        >
                                          <i className="fa-solid fa-users" style={{ fontSize: '12px' }}></i>
                                        </div>
                                        <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{row.tribeName}</span>
                                      </div>
                                    ) : (
                                      <div className="d-flex align-items-center gap-2">
                                        <div
                                          className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                          style={{ width: '32px', height: '32px', background: '#0ea5e9', fontSize: '11px' }}
                                        >
                                          <i className="fa fa-sitemap" style={{ fontSize: '12px' }}></i>
                                        </div>
                                        <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{row.groupName}</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center fw-bold bg-light text-dark" style={{ position: 'sticky', left: '220px', backgroundColor: '#f8fafc', zIndex: 2, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)', width: '100px', minWidth: '100px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{formattedRowTotal}</td>
                                {row.days.map((d: any, index: number) => {
                                  let rawVal = matrixMetricMode === 'percentage'
                                    ? (matrixValueMode === 'cumulative'
                                      ? row.days.slice(0, index + 1).reduce((sum: number, day: any) => sum + (day.percentage || 0), 0)
                                      : (d.percentage || 0))
                                    : (matrixValueMode === 'cumulative'
                                      ? row.days.slice(0, index + 1).reduce((sum: number, day: any) => sum + day.count, 0)
                                      : d.count);
                                  const displayVal = matrixMetricMode === 'percentage'
                                    ? (rawVal === 0 ? '0%' : `${(rawVal * 100).toFixed(2)}%`)
                                    : rawVal;
                                  const isInactive = d.count === 0;
                                  return (
                                    <td
                                      key={`${row.userId || row.tribeName || row.groupName}-${d.date}-${index}`}
                                      className="text-center"
                                      style={{
                                        minWidth: '85px',
                                        whiteSpace: 'nowrap',
                                        borderRight: '1px solid #e2e8f0',
                                        borderBottom: '1px solid #e2e8f0',
                                        backgroundColor: isInactive ? '#fff0f2' : 'transparent',
                                        color: isInactive ? '#e11d48' : 'inherit'
                                      }}
                                    >
                                      <div className="fw-semibold">{displayVal}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="animate__animated animate__fadeIn p-2" style={{ height: '400px', position: 'relative' }}>
                    <Line data={matrixChartData} options={matrixChartOptions} plugins={[verticalHoverPlugin]} />
                  </div>
                )
              ) : (
                <div className="text-center py-4 text-muted">No daily matrix data available.</div>
              )}
            </div>
          </div>
        </div>
      }
      <UserDetailModal
        show={showDetailModal}
        onClose={handleCloseModal}
        userId={selectedOfficerId || undefined}
        token={token}
      />

      <AreaDetailModal
        show={showAreaDetail}
        onClose={() => setShowAreaDetail(false)}
        areaName={selectedAreaName}
        areaDescription={selectedAreaDescription}
        projectId={selectedProject}
        data={areaDetailData}
        loading={loadingAreaDetail}
      />

      {showResultDetailModal && selectedResultItem && (
        <DashboardDetailModal
          item={selectedResultItem}
          onClose={() => {
            setShowResultDetailModal(false);
            setSelectedResultItem(null);
          }}
        />
      )}

      {showEndProjectModal && selectedProjectObj && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={handleCloseEndProjectModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content p-4" style={{ borderRadius: '16px' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 text-danger d-flex align-items-center gap-2">
                  <i className="fa fa-exclamation-triangle"></i> End Project Confirmation
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEndProjectModal}
                  disabled={isEndingProject}
                />
              </div>

              <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                Tindakan ini akan menghentikan pengerjaan project <strong>"{selectedProjectObj.name}"</strong> dan menghapus task-task project terkait dari aplikasi <b>More Access</b>.
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold text-muted" style={{ fontSize: '0.875rem' }}>
                  Ketik nama project untuk konfirmasi:
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={selectedProjectObj.name}
                  value={confirmProjectNameInput}
                  onChange={(e) => {
                    setConfirmProjectNameInput(e.target.value);
                    if (endProjectError) setEndProjectError(null);
                  }}
                  disabled={isEndingProject}
                  autoFocus
                />
                <div className="form-text text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                  Ketik <strong>{selectedProjectObj.name}</strong> untuk mengaktifkan tombol konfirmasi.
                </div>
              </div>

              {endProjectError && (
                <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
                  {endProjectError}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleCloseEndProjectModal}
                  disabled={isEndingProject}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmEndProject}
                  disabled={
                    isEndingProject ||
                    confirmProjectNameInput.trim() !== selectedProjectObj.name.trim()
                  }
                >
                  {isEndingProject ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    'Confirm End Project'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Monitor;